import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Plus,
  Trash2,
  Edit,
  Trophy,
  Clock,
  Gift,
  Users,
  Play,
  Pause,
  CheckCircle,
  AlertCircle,
  Copy,
  ExternalLink,
  Sparkles,
  Crown,
  Timer,
  FileText,
  HelpCircle,
  Flag,
  Calendar,
  Shuffle,
  Handshake,
  Youtube,
  Download,
  Link,
  Mail,
  Bell,
  Send,
  Building,
  AtSign,
  XCircle,
  Wand2,
  Lock,
  Unlock,
  Key,
  Eye,
  EyeOff,
  Settings,
  BarChart,
  Save,
  Target,
  Gamepad2,
  Loader2,
  Volume2,
  Music,
  Search,
  Upload
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';

const CMSGameContestsPage = () => {
  const [contests, setContests] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [players, setPlayers] = useState([]);
  const [winners, setWinners] = useState([]);
  const [rules, setRules] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [selectedContest, setSelectedContest] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  // Main section: 'annual' for Le Coffre MSC, 'ponctuel' for other games
  const [mainSection, setMainSection] = useState('annual');
  const [activeTab, setActiveTab] = useState('config');
  const [playersSubTab, setPlayersSubTab] = useState('pending'); // 'pending', 'validated', 'pseudo'
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiCount, setAiCount] = useState(5);
  const [aiDifficulty, setAiDifficulty] = useState('medium');
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  // Annual leaderboard state
  const [annualYear, setAnnualYear] = useState(new Date().getFullYear());
  const [annualLeaderboard, setAnnualLeaderboard] = useState([]);
  const [periodicGames, setPeriodicGames] = useState([]);
  
  // Annual Chest Game state
  const [annualGame, setAnnualGame] = useState(null);
  const [annualGameDialogOpen, setAnnualGameDialogOpen] = useState(false);
  const [annualGameForm, setAnnualGameForm] = useState({
    year: new Date().getFullYear(),
    name: 'Le Coffre MSC',
    winning_combination: '',
    prize: '',
    prize_value: '',
    prize_description: '',
    secret_hints: Array(12).fill(null).map((_, i) => ({ month: i + 1, hint: '' })),
    auto_create_quizzes: true,
    quiz_topic: 'services de l\'agence MSC',
    theme_audio_url: ''
  });
  const [annualPlayersProgress, setAnnualPlayersProgress] = useState([]);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  
  // Annual Game Management States
  const [annualPlayers, setAnnualPlayers] = useState([]);
  const [annualQuestions, setAnnualQuestions] = useState([]);
  const [annualRules, setAnnualRules] = useState('');
  const [annualStats, setAnnualStats] = useState(null);
  const [annualManagementTab, setAnnualManagementTab] = useState('players');
  const [generatingRules, setGeneratingRules] = useState(false);
  const [loadingAnnualData, setLoadingAnnualData] = useState(false);
  
  // Nouveau Coffre states
  const [newCoffreYear, setNewCoffreYear] = useState(new Date().getFullYear() + 1);
  const [newCoffreName, setNewCoffreName] = useState('Le Coffre MSC');
  
  // Tirage au sort states
  const [drawModalOpen, setDrawModalOpen] = useState(false);
  const [drawContest, setDrawContest] = useState(null);
  const [drawParticipants, setDrawParticipants] = useState([]);
  const [drawResult, setDrawResult] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawAnimation, setDrawAnimation] = useState(false);
  
  // Reset game data states
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetPlayers, setResetPlayers] = useState(true);
  const [resetScores, setResetScores] = useState(true);
  const [resetAccounts, setResetAccounts] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);
  
  // Email history states
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSearch, setEmailSearch] = useState('');
  const [emailTypeFilter, setEmailTypeFilter] = useState('');
  const [emailPage, setEmailPage] = useState(0);
  const [showEmailContent, setShowEmailContent] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    prize: '',
    prize_description: '',
    prize_value: '', // Valeur du lot en €HT
    start_date: '',
    end_date: '',
    time_limit_seconds: 60,
    points_per_correct: 10,
    min_success_rate: 100, // Taux de réussite minimum (en %)
    time_bonus_enabled: true,
    is_main_game: false,
    game_type: 'ponctuel',
    reward_mode: 'classement',
    year: new Date().getFullYear(),
    month: 0,
    rattrapage_for_month: 0, // For catch-up quizzes
    // Partnership fields
    is_partnership: false,
    partner_name: '',
    partner_logo_url: '',
    partner_website: '',
    partner_description: '',
    youtube_video_url: '',
    // Site quiz
    is_site_quiz: false,
    // Tirage au sort settings
    draw_datetime: '',
    draw_location: '',
    draw_results_url: '',
    draw_reminder_message: ''
  });

  const [questionFormData, setQuestionFormData] = useState({
    question_text: '',
    question_type: 'text',
    media_url: '',
    options: [
      { text: '', is_correct: true },
      { text: '', is_correct: false },
      { text: '', is_correct: false },
      { text: '', is_correct: false }
    ],
    points: 10,
    order: 0,
    // Site quiz fields
    explanation: '',
    page_link: '',
    page_link_text: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contestsRes, playersRes, winnersRes, rulesRes] = await Promise.all([
        api.get('/game/cms/contests'),
        api.get('/game/cms/players'),
        api.get('/game/cms/winners'),
        api.get('/game/cms/rules')
      ]);
      setContests(contestsRes.data || []);
      setPlayers(playersRes.data || []);
      setWinners(winnersRes.data || []);
      setRules(rulesRes.data?.content || '');
    } catch (err) {
      toast.error('Erreur lors du chargement des données');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnualData = async (year) => {
    try {
      const [leaderboardRes, gamesRes] = await Promise.all([
        api.get(`/game/leaderboard/annual/${year}`),
        api.get(`/game/periodic-games/${year}`)
      ]);
      setAnnualLeaderboard(leaderboardRes.data || []);
      setPeriodicGames(gamesRes.data || []);
      
      // Fetch annual chest game data
      await fetchAnnualGameData(year);
    } catch (err) {
      console.error('Error fetching annual data:', err);
    }
  };
  
  const fetchAnnualGameData = async (year) => {
    try {
      const gameRes = await api.get(`/game/cms/annual-games/${year}`);
      setAnnualGame(gameRes.data);
      
      // Fetch players progress
      const progressRes = await api.get(`/game/cms/annual-games/${year}/progress`);
      setAnnualPlayersProgress(progressRes.data || []);
    } catch (err) {
      // No game for this year yet
      setAnnualGame(null);
      setAnnualPlayersProgress([]);
    }
  };
  
  const handleSaveAnnualGame = async () => {
    try {
      if (annualGameForm.winning_combination.length !== 24) {
        toast.error('La combinaison doit contenir exactement 24 caractères');
        return;
      }
      
      // Filter out empty hints
      const hints = annualGameForm.secret_hints.filter(h => h.hint && h.hint.trim());
      
      if (annualGame) {
        // Update existing - send ALL fields
        await api.put(`/game/cms/annual-games/${annualGameForm.year}`, {
          name: annualGameForm.name,
          winning_combination: annualGameForm.winning_combination,
          prize: annualGameForm.prize,
          prize_description: annualGameForm.prize_description,
          prize_value: annualGameForm.prize_value ? parseFloat(annualGameForm.prize_value) : null,
          secret_hints: hints,
          theme_audio_url: annualGameForm.theme_audio_url || null
        });
        toast.success('Jeu Annuel mis à jour !');
      } else {
        // Create new
        const res = await api.post('/game/cms/annual-games', {
          year: annualGameForm.year,
          name: annualGameForm.name,
          winning_combination: annualGameForm.winning_combination,
          prize: annualGameForm.prize,
          prize_description: annualGameForm.prize_description,
          prize_value: annualGameForm.prize_value ? parseFloat(annualGameForm.prize_value) : null,
          secret_hints: hints,
          auto_create_quizzes: annualGameForm.auto_create_quizzes,
          quiz_topic: annualGameForm.quiz_topic
        });
        toast.success(res.data.message || 'Jeu Annuel créé !');
      }
      
      setAnnualGameDialogOpen(false);
      fetchAnnualData(annualYear);
      fetchData(); // Refresh contests list to show new quizzes
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };
  
  // Generate random 24-character combination
  const generateRandomCombination = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let combo = '';
    for (let i = 0; i < 24; i++) {
      combo += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setAnnualGameForm({...annualGameForm, winning_combination: combo});
  };
  
  // Generate AI questions for all monthly quizzes
  const handleGenerateAnnualQuestions = async () => {
    if (!annualGame) {
      toast.error('Créez d\'abord le Jeu Annuel');
      return;
    }
    
    setGeneratingQuestions(true);
    try {
      const res = await api.post(`/game/cms/annual-games/${annualYear}/generate-questions?topic=${encodeURIComponent(annualGameForm.quiz_topic || 'services de l\'agence MSC')}`);
      toast.success(res.data.message);
      fetchAnnualData(annualYear);
      fetchAnnualManagementData(annualYear);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setGeneratingQuestions(false);
    }
  };

  // Fetch annual management data (players, questions, rules, stats)
  const fetchAnnualManagementData = async (year) => {
    setLoadingAnnualData(true);
    try {
      // Fetch players
      const playersRes = await api.get(`/game/cms/annual-games/${year}/players`);
      setAnnualPlayers(playersRes.data.players || []);
      
      // Fetch questions
      const questionsRes = await api.get(`/game/cms/annual-games/${year}/questions`);
      setAnnualQuestions(questionsRes.data.months || []);
      
      // Fetch rules
      const rulesRes = await api.get(`/game/cms/annual-games/${year}/rules`);
      setAnnualRules(rulesRes.data.rules || '');
      
      // Fetch stats
      const statsRes = await api.get(`/game/cms/annual-games/${year}/stats`);
      setAnnualStats(statsRes.data);
    } catch (err) {
      console.log('Error fetching annual management data:', err);
    } finally {
      setLoadingAnnualData(false);
    }
  };

  // Generate rules
  const handleGenerateRules = async () => {
    setGeneratingRules(true);
    try {
      const res = await api.post(`/game/cms/annual-games/${annualYear}/generate-rules`);
      setAnnualRules(res.data.rules || '');
      toast.success('Règlement généré avec succès !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la génération du règlement');
    } finally {
      setGeneratingRules(false);
    }
  };

  // Save rules
  const handleSaveRules = async () => {
    try {
      await api.put(`/game/cms/annual-games/${annualYear}/rules`, { rules: annualRules });
      toast.success('Règlement sauvegardé !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };
  
  // Reset game data
  const handleResetGameData = async () => {
    if (resetConfirmText !== 'CONFIRMER') {
      toast.error("Tapez 'CONFIRMER' pour valider");
      return;
    }
    
    setResetting(true);
    try {
      const res = await api.post(`/game/cms/annual-games/${annualYear}/reset`, {
        reset_players: resetPlayers,
        reset_scores: resetScores,
        reset_accounts: resetAccounts,
        confirm: 'CONFIRMER'
      });
      
      toast.success(res.data.message || 'Données réinitialisées');
      setResetDialogOpen(false);
      setResetConfirmText('');
      setResetAccounts(false);
      
      // Refresh data
      fetchAnnualData(annualYear);
      fetchAnnualManagementData(annualYear);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setResetting(false);
    }
  };
  
  // Fetch email history
  const fetchEmailHistory = async (search = '', emailType = '', page = 0) => {
    setEmailLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '20');
      params.append('offset', String(page * 20));
      if (search) params.append('search', search);
      if (emailType) params.append('email_type', emailType);
      
      const [historyRes, statsRes] = await Promise.all([
        api.get(`/game/cms/emails?${params.toString()}`),
        api.get('/game/cms/emails/stats')
      ]);
      
      setEmailHistory(historyRes.data.emails || []);
      setEmailStats(statsRes.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des emails');
    } finally {
      setEmailLoading(false);
    }
  };
  
  const handleEditAnnualGame = () => {
    if (annualGame) {
      // Pre-fill form with existing data
      const hints = Array(12).fill(null).map((_, i) => {
        const existing = annualGame.secret_hints?.find(h => h.month === i + 1);
        return { month: i + 1, hint: existing?.hint || '' };
      });
      
      setAnnualGameForm({
        year: annualGame.year,
        name: annualGame.name || 'Le Coffre MSC',
        winning_combination: annualGame.winning_combination || '',
        prize: annualGame.prize || '',
        prize_description: annualGame.prize_description || '',
        secret_hints: hints,
        auto_create_quizzes: false, // Don't recreate quizzes on edit
        quiz_topic: 'services de l\'agence MSC'
      });
    } else {
      // Reset form for new game
      setAnnualGameForm({
        year: annualYear,
        name: 'Le Coffre MSC',
        winning_combination: '',
        prize: '',
        prize_description: '',
        secret_hints: Array(12).fill(null).map((_, i) => ({ month: i + 1, hint: '' })),
        auto_create_quizzes: true,
        quiz_topic: 'services de l\'agence MSC'
      });
    }
    setAnnualGameDialogOpen(true);
  };

  // Load annual data when tab changes or year changes
  useEffect(() => {
    if (mainSection === 'annual') {
      fetchAnnualData(annualYear);
      fetchAnnualManagementData(annualYear);
    }
  }, [mainSection, annualYear]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync annualGameForm when annualGame data is loaded
  useEffect(() => {
    if (annualGame) {
      const hints = Array(12).fill(null).map((_, i) => {
        const existing = annualGame.secret_hints?.find(h => h.month === i + 1);
        return { month: i + 1, hint: existing?.hint || '' };
      });
      
      setAnnualGameForm({
        year: annualGame.year,
        name: annualGame.name || 'Le Coffre MSC',
        winning_combination: annualGame.winning_combination || '',
        prize: annualGame.prize || '',
        prize_value: annualGame.prize_value || '',
        prize_description: annualGame.prize_description || '',
        secret_hints: hints,
        auto_create_quizzes: false,
        quiz_topic: 'services de l\'agence MSC',
        theme_audio_url: annualGame.theme_audio_url || ''
      });
    }
  }, [annualGame]);

  // Load questions when selected contest changes
  useEffect(() => {
    if (selectedContest?.id && activeTab === 'questions') {
      fetchQuestions(selectedContest.id);
    }
  }, [selectedContest?.id, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchQuestions = async (gameId) => {
    try {
      const res = await api.get(`/game/cms/questions?game_id=${gameId}`);
      setQuestions(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateContest = async () => {
    try {
      await api.post('/game/cms/contests', formData);
      toast.success('Jeu concours créé avec succès');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateContest = async () => {
    try {
      await api.put(`/game/cms/contests/${selectedContest.id}`, formData);
      toast.success('Jeu concours mis à jour');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteContest = async (id) => {
    if (!window.confirm('Supprimer ce jeu concours et toutes ses questions ?')) return;
    try {
      await api.delete(`/game/cms/contests/${id}`);
      toast.success('Jeu concours supprimé');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleFinishContest = async (id) => {
    if (!window.confirm('Terminer ce jeu et désigner les gagnants ?')) return;
    try {
      await api.post(`/game/cms/contests/${id}/finish`);
      toast.success('Jeu terminé, gagnants notifiés par email');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la clôture');
    }
  };

  const handleCreateQuestion = async () => {
    if (!selectedContest) return;
    try {
      await api.post('/game/cms/questions', {
        ...questionFormData,
        game_id: selectedContest.id
      });
      toast.success('Question créée');
      setQuestionDialogOpen(false);
      resetQuestionForm();
      fetchQuestions(selectedContest.id);
    } catch (err) {
      toast.error('Erreur lors de la création de la question');
    }
  };

  const handleUpdateQuestion = async () => {
    try {
      await api.put(`/game/cms/questions/${selectedQuestion.id}`, questionFormData);
      toast.success('Question mise à jour');
      setQuestionDialogOpen(false);
      resetQuestionForm();
      if (selectedContest) fetchQuestions(selectedContest.id);
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteQuestion = async (id) => {
    if (!window.confirm('Supprimer cette question ?')) return;
    try {
      await api.delete(`/game/cms/questions/${id}`);
      toast.success('Question supprimée');
      if (selectedContest) fetchQuestions(selectedContest.id);
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleValidatePlayer = async (playerId) => {
    try {
      await api.put(`/game/cms/players/${playerId}/validate`);
      toast.success('Joueur validé et notifié par email');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleTogglePlayerActive = async (playerId) => {
    try {
      await api.put(`/game/cms/players/${playerId}/toggle-active`);
      toast.success('Statut du joueur modifié');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDeletePlayer = async (playerId) => {
    if (!window.confirm('Supprimer définitivement ce joueur ?')) return;
    try {
      await api.delete(`/game/cms/players/${playerId}`);
      toast.success('Joueur supprimé');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleValidatePseudo = async (playerId) => {
    try {
      await api.put(`/game/cms/players/${playerId}/validate-pseudo`);
      toast.success('Pseudo validé');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la validation du pseudo');
    }
  };

  const handleRejectPseudo = async (playerId) => {
    if (!window.confirm('Rejeter et supprimer ce pseudo ?')) return;
    try {
      await api.put(`/game/cms/players/${playerId}/reject-pseudo`);
      toast.success('Pseudo rejeté');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors du rejet du pseudo');
    }
  };

  // ============================================
  // EMAIL NOTIFICATION FUNCTIONS
  // ============================================
  const [sendingNotification, setSendingNotification] = useState(false);

  // ============================================
  // CREATE NEW ANNUAL GAME
  // ============================================
  const handleCreateNewAnnualGame = async (year, name) => {
    setLoading(true);
    try {
      const res = await api.post('/game/cms/annual-games/create-empty', { year, name });
      toast.success(res.data.message);
      setAnnualYear(year);
      await fetchAnnualData(year);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handleNotifyNewHint = async (month) => {
    if (!window.confirm(`Envoyer une notification à tous les joueurs pour l'indice de ${['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'][month]} ?`)) return;
    setSendingNotification(true);
    try {
      const res = await api.post('/game/cms/notify/new-hint', { month, year: annualYear });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingNotification(false);
    }
  };

  const handleNotifyNewQuiz = async (quizId) => {
    if (!window.confirm('Envoyer une notification à tous les joueurs pour ce nouveau quiz ?')) return;
    setSendingNotification(true);
    try {
      const res = await api.post('/game/cms/notify/new-quiz', { quiz_id: quizId });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingNotification(false);
    }
  };

  const handleNotifyNewContest = async (contestId) => {
    if (!window.confirm('Envoyer une notification à tous les joueurs pour ce nouveau jeu ?')) return;
    setSendingNotification(true);
    try {
      const res = await api.post('/game/cms/notify/new-contest', { contest_id: contestId });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingNotification(false);
    }
  };

  const handleNotifyGameResults = async (contestId) => {
    if (!window.confirm('Envoyer les résultats à tous les participants ?')) return;
    setSendingNotification(true);
    try {
      const res = await api.post('/game/cms/notify/game-results', { contest_id: contestId });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingNotification(false);
    }
  };

  const handleNotifyCoffreWinner = async (playerId) => {
    if (!window.confirm('Envoyer l\'email de félicitations au gagnant du Coffre MSC ?')) return;
    setSendingNotification(true);
    try {
      const res = await api.post('/game/cms/notify/coffre-winner', { player_id: playerId, year: annualYear });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingNotification(false);
    }
  };

  // ==================== TIRAGE AU SORT FUNCTIONS ====================
  
  const openDrawModal = async (contest) => {
    setDrawContest(contest);
    setDrawResult(null);
    setIsDrawing(false);
    setDrawAnimation(false);
    
    try {
      const res = await api.get(`/game/cms/contests/${contest.id}/draw-preview`);
      setDrawParticipants(res.data.participants || []);
      setDrawModalOpen(true);
    } catch (err) {
      toast.error('Erreur lors du chargement des participants');
    }
  };

  const performDraw = async () => {
    if (!drawContest) return;
    
    setIsDrawing(true);
    setDrawAnimation(true);
    
    // Animation de suspense (3 secondes)
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    try {
      const res = await api.post(`/game/cms/contests/${drawContest.id}/draw`);
      setDrawResult(res.data);
      toast.success('🎉 Tirage au sort effectué !');
      fetchData(); // Refresh the list
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du tirage');
    } finally {
      setIsDrawing(false);
      setDrawAnimation(false);
    }
  };

  const sendDrawReminder = async (contestId, reminderType) => {
    const typeLabels = { '7days': 'J-7', '1day': 'J-1', 'today': 'Jour J' };
    if (!window.confirm(`Envoyer les rappels ${typeLabels[reminderType]} à tous les joueurs validés ?`)) return;
    
    setSendingNotification(true);
    try {
      const res = await api.post(`/game/cms/contests/${contestId}/send-draw-reminders`, { reminder_type: reminderType });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingNotification(false);
    }
  };

  const [rulesGenerating, setRulesGenerating] = useState(false);
  
  const handleGenerateContestRules = async () => {
    if (!selectedContest) {
      toast.error('Sélectionnez d\'abord un jeu');
      return;
    }
    setRulesGenerating(true);
    try {
      const res = await api.post('/game/cms/rules/generate', {
        game_title: selectedContest.title,
        prize: selectedContest.prize,
        prize_description: selectedContest.prize_description || '',
        start_date: selectedContest.start_date || '',
        end_date: selectedContest.end_date || ''
      });
      setRules(res.data.content);
      toast.success('Règlement généré ! N\'oubliez pas de l\'enregistrer.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setRulesGenerating(false);
    }
  };

  const handleGenerateQuestions = async () => {
    if (!aiTopic.trim()) {
      toast.error('Veuillez entrer un sujet');
      return;
    }
    setAiLoading(true);
    try {
      const res = await api.post('/game/cms/questions/generate', {
        topic: aiTopic,
        count: aiCount,
        difficulty: aiDifficulty
      });
      setGeneratedQuestions(res.data.questions || []);
      toast.success(`${res.data.questions?.length || 0} questions générées !`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setAiLoading(false);
    }
  };

  const handleAddGeneratedQuestion = async (question) => {
    if (!selectedContest) {
      toast.error('Sélectionnez d\'abord un jeu concours');
      return;
    }
    try {
      await api.post('/game/cms/questions', {
        game_id: selectedContest.id,
        question_text: question.question_text,
        question_type: 'text',
        options: question.options,
        points: question.points || 10,
        order: questions.length
      });
      toast.success('Question ajoutée au jeu');
      fetchQuestions(selectedContest.id);
      setGeneratedQuestions(prev => prev.filter(q => q !== question));
    } catch (err) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      prize: '',
      prize_description: '',
      prize_value: '',
      start_date: '',
      end_date: '',
      time_limit_seconds: 60,
      points_per_correct: 10,
      min_success_rate: 100,
      reward_character: '',
      time_bonus_enabled: true,
      is_main_game: false,
      game_type: 'ponctuel',
      reward_mode: 'classement',
      year: new Date().getFullYear(),
      month: 0,
      rattrapage_for_month: 0,
      is_partnership: false,
      partner_name: '',
      partner_logo_url: '',
      partner_website: '',
      partner_description: '',
      youtube_video_url: '',
      is_site_quiz: false,
      // Tirage au sort
      draw_datetime: '',
      draw_location: '',
      draw_results_url: '',
      draw_reminder_message: ''
    });
    setSelectedContest(null);
  };

  const resetQuestionForm = () => {
    setQuestionFormData({
      question_text: '',
      question_type: 'text',
      media_url: '',
      options: [
        { text: '', is_correct: true },
        { text: '', is_correct: false },
        { text: '', is_correct: false },
        { text: '', is_correct: false }
      ],
      points: 10,
      order: 0,
      explanation: '',
      page_link: '',
      page_link_text: ''
    });
    setSelectedQuestion(null);
  };

  const openEditContest = (contest) => {
    setSelectedContest(contest);
    setFormData({
      title: contest.title || '',
      description: contest.description || '',
      prize: contest.prize || '',
      prize_description: contest.prize_description || '',
      prize_value: contest.prize_value || '',
      start_date: contest.start_date?.split('T')[0] || '',
      end_date: contest.end_date?.split('T')[0] || '',
      time_limit_seconds: contest.time_limit_seconds || 60,
      points_per_correct: contest.points_per_correct || 10,
      min_success_rate: contest.min_success_rate || 100,
      reward_character: contest.reward_character || '',
      time_bonus_enabled: contest.time_bonus_enabled !== false,
      is_main_game: contest.is_main_game || false,
      game_type: contest.game_type || 'ponctuel',
      reward_mode: contest.reward_mode || 'classement',
      year: contest.year || new Date().getFullYear(),
      month: contest.month || 0,
      rattrapage_for_month: contest.rattrapage_for_month || 0,
      is_partnership: contest.is_partnership || false,
      partner_name: contest.partner_name || '',
      partner_logo_url: contest.partner_logo_url || '',
      partner_website: contest.partner_website || '',
      partner_description: contest.partner_description || '',
      youtube_video_url: contest.youtube_video_url || '',
      is_site_quiz: contest.is_site_quiz || false,
      // Tirage au sort
      draw_datetime: contest.draw_datetime || '',
      draw_location: contest.draw_location || '',
      draw_results_url: contest.draw_results_url || '',
      draw_reminder_message: contest.draw_reminder_message || ''
    });
    setDialogOpen(true);
  };

  const openEditQuestion = (question) => {
    setSelectedQuestion(question);
    
    // Support both formats: question_text and question
    const questionText = question.question_text || question.question || '';
    
    // Support both formats for options: strings array or objects array
    let formattedOptions;
    if (question.options?.[0] && typeof question.options[0] === 'object') {
      // Already in object format
      formattedOptions = question.options.map(opt => ({
        text: opt.text || '',
        is_correct: opt.is_correct || false
      }));
    } else {
      // String format - convert to objects
      const correctIndex = question.correct_answer || 0;
      formattedOptions = (question.options || []).map((opt, idx) => ({
        text: typeof opt === 'string' ? opt : '',
        is_correct: idx === correctIndex
      }));
    }
    
    // Ensure we have 4 options
    while (formattedOptions.length < 4) {
      formattedOptions.push({ text: '', is_correct: false });
    }
    
    setQuestionFormData({
      question_text: questionText,
      question_type: question.question_type || 'text',
      media_url: question.media_url || '',
      options: formattedOptions,
      points: question.points || 10,
      order: question.order || 0,
      explanation: question.explanation || '',
      page_link: question.page_link || '',
      page_link_text: question.page_link_text || ''
    });
    setQuestionDialogOpen(true);
  };

  const selectContestForQuestions = (contest) => {
    setSelectedContest(contest);
    fetchQuestions(contest.id);
    setActiveTab('questions');
  };

  const copyGameUrl = (contest) => {
    const url = `${window.location.origin}/jeu/${contest.id}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copiée !');
  };

  const pendingPlayers = players.filter(p => !p.is_validated);
  const validatedPlayers = players.filter(p => p.is_validated);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Trophy className="w-7 h-7 text-amber-500" />
            Jeux Concours
          </h1>
          <p className="text-muted-foreground">Gérez vos quiz et jeux concours</p>
        </div>
        
        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {/* Email History Button */}
          <Dialog>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                onClick={() => fetchEmailHistory()}
              >
                <Mail className="w-4 h-4 mr-2" />
                Historique Emails
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-500" />
                  Historique des Emails Envoyés
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
                {/* Stats */}
                {emailStats && (
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-600">{emailStats.total || 0}</p>
                      <p className="text-xs text-blue-600/70">Total envoyés</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-600">{emailStats.success || 0}</p>
                      <p className="text-xs text-green-600/70">Succès</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-red-600">{emailStats.failed || 0}</p>
                      <p className="text-xs text-red-600/70">Échecs</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-amber-600">{emailStats.recent_24h || 0}</p>
                      <p className="text-xs text-amber-600/70">Dernières 24h</p>
                    </div>
                  </div>
                )}
                
                {/* Filters */}
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Input
                      placeholder="Rechercher par email, nom, sujet..."
                      value={emailSearch}
                      onChange={(e) => setEmailSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchEmailHistory(emailSearch, emailTypeFilter, 0)}
                    />
                  </div>
                  <Select value={emailTypeFilter || 'all'} onValueChange={(v) => { setEmailTypeFilter(v === 'all' ? '' : v); fetchEmailHistory(emailSearch, v === 'all' ? '' : v, 0); }}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Type d'email" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      <SelectItem value="registration_pending">Inscription</SelectItem>
                      <SelectItem value="account_validated">Validation compte</SelectItem>
                      <SelectItem value="new_hint">Nouvel indice</SelectItem>
                      <SelectItem value="new_quiz">Nouveau quiz</SelectItem>
                      <SelectItem value="game_results">Résultats jeu</SelectItem>
                      <SelectItem value="winner">Gagnant</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={() => fetchEmailHistory(emailSearch, emailTypeFilter, 0)}>
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Email List */}
                <div className="flex-1 overflow-auto border rounded-lg">
                  {emailLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    </div>
                  ) : emailHistory.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Mail className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Aucun email dans l'historique</p>
                      <p className="text-sm">Les prochains emails envoyés apparaîtront ici</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Date/Heure</th>
                          <th className="px-3 py-2 text-left">Destinataire</th>
                          <th className="px-3 py-2 text-left">Sujet</th>
                          <th className="px-3 py-2 text-left">Type</th>
                          <th className="px-3 py-2 text-center">Statut</th>
                          <th className="px-3 py-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailHistory.map((email, idx) => (
                          <tr key={idx} className="border-t hover:bg-slate-50">
                            <td className="px-3 py-2">
                              <div className="text-xs">
                                {new Date(email.sent_at).toLocaleDateString('fr-FR')}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {new Date(email.sent_at).toLocaleTimeString('fr-FR')}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-medium">{email.player_name || '-'}</div>
                              <div className="text-xs text-muted-foreground">{email.to_email}</div>
                            </td>
                            <td className="px-3 py-2 max-w-xs truncate" title={email.subject}>
                              {email.subject}
                            </td>
                            <td className="px-3 py-2">
                              <Badge variant="outline" className="text-xs">
                                {email.email_type || 'general'}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 text-center">
                              {email.success ? (
                                <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                              ) : (
                                <XCircle className="w-4 h-4 text-red-500 mx-auto" />
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setShowEmailContent(email)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                
                {/* Pagination */}
                {emailHistory.length > 0 && (
                  <div className="flex justify-between items-center pt-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={emailPage === 0}
                      onClick={() => { setEmailPage(p => p - 1); fetchEmailHistory(emailSearch, emailTypeFilter, emailPage - 1); }}
                    >
                      Précédent
                    </Button>
                    <span className="text-sm text-muted-foreground">Page {emailPage + 1}</span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={emailHistory.length < 20}
                      onClick={() => { setEmailPage(p => p + 1); fetchEmailHistory(emailSearch, emailTypeFilter, emailPage + 1); }}
                    >
                      Suivant
                    </Button>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Email Content Dialog */}
          <Dialog open={!!showEmailContent} onOpenChange={(open) => !open && setShowEmailContent(null)}>
            <DialogContent className="max-w-3xl max-h-[80vh]">
              <DialogHeader>
                <DialogTitle>Contenu de l'email</DialogTitle>
              </DialogHeader>
              {showEmailContent && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Destinataire:</span>
                      <p className="font-medium">{showEmailContent.to_email}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Date/Heure:</span>
                      <p className="font-medium">{new Date(showEmailContent.sent_at).toLocaleString('fr-FR')}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Sujet:</span>
                      <p className="font-medium">{showEmailContent.subject}</p>
                    </div>
                  </div>
                  <div className="border rounded-lg p-4 bg-white max-h-96 overflow-auto">
                    <div dangerouslySetInnerHTML={{ __html: showEmailContent.html_content }} />
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
          
          {/* Reset Button */}
          <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" />
                Reset Jeu
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-red-600 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Réinitialiser les données du Coffre MSC {annualYear}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800 font-medium">⚠️ ATTENTION : Cette action est irréversible !</p>
                  <p className="text-xs text-red-600 mt-1">
                    Les données supprimées ne pourront pas être récupérées.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <Label className="font-semibold">Données à supprimer :</Label>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={resetPlayers}
                      onCheckedChange={setResetPlayers}
                    />
                    <span className="text-sm">Progression des joueurs (combinaisons, caractères découverts)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={resetScores}
                      onCheckedChange={setResetScores}
                    />
                    <span className="text-sm">Scores des quiz mensuels (classements)</span>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-red-200">
                    <Switch
                      checked={resetAccounts}
                      onCheckedChange={setResetAccounts}
                    />
                    <span className="text-sm text-red-600 font-medium">⚠️ Supprimer tous les comptes joueurs (emails)</span>
                  </div>
                  {resetAccounts && (
                    <p className="text-xs text-red-500 bg-red-50 p-2 rounded">
                      Attention : Cette action supprimera tous les comptes joueurs. Ils devront se réinscrire.
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm">Tapez <strong className="text-red-600">CONFIRMER</strong> pour valider :</Label>
                  <Input
                    value={resetConfirmText}
                    onChange={(e) => setResetConfirmText(e.target.value)}
                    placeholder="CONFIRMER"
                    className="border-red-200 focus:border-red-500"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setResetDialogOpen(false);
                  setResetConfirmText('');
                  setResetAccounts(false);
                }}>
                  Annuler
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleResetGameData}
                  disabled={resetting || resetConfirmText !== 'CONFIRMER' || (!resetPlayers && !resetScores && !resetAccounts)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {resetting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Suppression...</>
                  ) : (
                    <><Trash2 className="w-4 h-4 mr-2" /> Supprimer définitivement</>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      {/* Main Section Tabs: Jeu Annuel vs Jeux Ponctuels */}
      <div className="flex gap-3 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl">
        <button
          onClick={() => {
            setMainSection('annual');
            setActiveTab('config'); // Reset to first tab for annual
          }}
          className={`
            flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-lg font-semibold transition-all
            ${mainSection === 'annual' 
              ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg' 
              : 'text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-700'
            }
          `}
        >
          <img src="/images/coffre-fort.png" alt="" className={`w-8 h-8 object-contain ${mainSection === 'annual' ? 'brightness-0 invert' : ''}`} />
          <div className="text-left">
            <div className="font-bold">Le Coffre MSC {annualYear}</div>
            <div className="text-xs opacity-80">Jeu Annuel</div>
          </div>
        </button>
        
        <button
          onClick={() => {
            setMainSection('ponctuel');
            setActiveTab('contests'); // Reset to first tab for ponctuels
          }}
          className={`
            flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-lg font-semibold transition-all
            ${mainSection === 'ponctuel' 
              ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg' 
              : 'text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-700'
            }
          `}
        >
          <Gamepad2 className="w-8 h-8" />
          <div className="text-left">
            <div className="font-bold">Jeux Ponctuels</div>
            <div className="text-xs opacity-80">{contests.filter(c => c.game_type !== 'periodique').length} jeux</div>
          </div>
        </button>
      </div>

      {/* ============================================ */}
      {/* SECTION: GRAND JEU ANNUEL - LE COFFRE MSC */}
      {/* ============================================ */}
      {mainSection === 'annual' && (
        <div className="space-y-6">
          {/* Sélecteur d'année et bouton Nouveau Coffre */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Label className="text-sm text-muted-foreground">Année :</Label>
              <Select value={String(annualYear)} onValueChange={(v) => setAnnualYear(parseInt(v))}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {annualGame && (
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  <Trophy className="w-3 h-3 mr-1" />
                  {annualGame.name || 'Le Coffre MSC'}
                </Badge>
              )}
            </div>
            
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau Coffre
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-amber-500" />
                    Créer un nouveau Coffre MSC
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Année du jeu</Label>
                    <Select 
                      value={String(newCoffreYear)} 
                      onValueChange={(v) => setNewCoffreYear(parseInt(v))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2025, 2026, 2027, 2028, 2029, 2030].map(y => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Nom du jeu</Label>
                    <Input
                      value={newCoffreName}
                      onChange={(e) => setNewCoffreName(e.target.value)}
                      placeholder="Ex: Le Coffre MSC"
                    />
                    <p className="text-xs text-muted-foreground">
                      Le nom complet sera : "{newCoffreName} {newCoffreYear}"
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button 
                    onClick={() => handleCreateNewAnnualGame(newCoffreYear, newCoffreName)}
                    disabled={loading}
                    className="bg-amber-500 hover:bg-amber-600"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Création...</>
                    ) : (
                      <><Plus className="w-4 h-4 mr-2" /> Créer le Coffre</>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-6 bg-amber-50 dark:bg-amber-900/20">
              <TabsTrigger value="config" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                <Settings className="w-4 h-4 mr-2" /> Config
              </TabsTrigger>
              <TabsTrigger value="questions" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                <HelpCircle className="w-4 h-4 mr-2" /> Questions
              </TabsTrigger>
              <TabsTrigger value="players" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                <Users className="w-4 h-4 mr-2" /> Joueurs
              </TabsTrigger>
              <TabsTrigger value="rules" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                <FileText className="w-4 h-4 mr-2" /> Règlement
              </TabsTrigger>
              <TabsTrigger value="winners" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                <Trophy className="w-4 h-4 mr-2" /> Gagnants
              </TabsTrigger>
              <TabsTrigger value="results" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                <Target className="w-4 h-4 mr-2" /> Résultats
              </TabsTrigger>
            </TabsList>
            
            {/* Configuration Tab */}
            <TabsContent value="config" className="mt-6">
              <Card className="border-amber-200 dark:border-amber-800">
                <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
                  <CardTitle className="flex items-center gap-3">
                    <img src="/images/coffre-fort.png" alt="" className="w-10 h-10" />
                    Configuration du Coffre MSC {annualYear}
                  </CardTitle>
                  <CardDescription>
                    Configurez le jeu annuel : lot, combinaison secrète, indices
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  {annualGame ? (
                    <>
                      {/* Nom du jeu (éditable) */}
                      <div className="space-y-2">
                        <Label>📝 Nom du jeu</Label>
                        <Input
                          value={annualGameForm.name || ''}
                          onChange={(e) => setAnnualGameForm({...annualGameForm, name: e.target.value})}
                          placeholder="Le Coffre MSC"
                        />
                      </div>
                      
                      {/* Gros Lot */}
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label>🏆 Nom du Gros Lot</Label>
                          <Input
                            value={annualGameForm.prize}
                            onChange={(e) => setAnnualGameForm({...annualGameForm, prize: e.target.value})}
                            placeholder="Ex: 24 Heures de création + 1 site vitrine"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>💰 Valeur (€HT)</Label>
                          <Input
                            type="number"
                            value={annualGameForm.prize_value || ''}
                            onChange={(e) => setAnnualGameForm({...annualGameForm, prize_value: parseFloat(e.target.value) || 0})}
                            placeholder="Ex: 2500"
                          />
                        </div>
                        <div className="col-span-2 space-y-2">
                          <Label>Description du lot</Label>
                          <Textarea
                            value={annualGameForm.prize_description}
                            onChange={(e) => setAnnualGameForm({...annualGameForm, prize_description: e.target.value})}
                            placeholder="Décrivez le lot en détail..."
                            rows={2}
                          />
                        </div>
                        
                        {/* Audio Theme */}
                        <div className="col-span-2 space-y-2">
                          <Label className="flex items-center gap-2">
                            <Volume2 className="w-4 h-4" /> Musique d'ambiance (page Le Coffre)
                          </Label>
                          <div className="flex items-center gap-3">
                            {annualGameForm.theme_audio_url ? (
                              <div className="flex-1 flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                                <Music className="w-5 h-5 text-green-500" />
                                <span className="text-sm flex-1 truncate">{annualGameForm.theme_audio_url}</span>
                                <audio src={annualGameForm.theme_audio_url} controls className="h-8" />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                  onClick={async () => {
                                    if (confirm('Supprimer cet audio ?')) {
                                      try {
                                        await api.delete(`/game/cms/annual-games/${annualGameForm.year}/audio`);
                                        setAnnualGameForm({...annualGameForm, theme_audio_url: ''});
                                        toast.success('Audio supprimé');
                                      } catch (err) {
                                        toast.error('Erreur lors de la suppression');
                                      }
                                    }
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex-1">
                                <input
                                  type="file"
                                  accept="audio/mp3,audio/mpeg,audio/wav,audio/ogg,audio/x-m4a"
                                  className="hidden"
                                  id="audio-upload"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    
                                    setUploadingAudio(true);
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    
                                    try {
                                      const res = await api.post(
                                        `/game/cms/annual-games/${annualGameForm.year}/upload-audio`,
                                        formData,
                                        { headers: { 'Content-Type': 'multipart/form-data' } }
                                      );
                                      setAnnualGameForm({...annualGameForm, theme_audio_url: res.data.audio_url});
                                      toast.success('Audio uploadé avec succès !');
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Erreur lors de l\'upload');
                                    } finally {
                                      setUploadingAudio(false);
                                    }
                                  }}
                                />
                                <label
                                  htmlFor="audio-upload"
                                  className={`flex items-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors ${uploadingAudio ? 'opacity-50 pointer-events-none' : ''}`}
                                >
                                  {uploadingAudio ? (
                                    <>
                                      <Loader2 className="w-5 h-5 animate-spin" />
                                      <span>Upload en cours...</span>
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="w-5 h-5 text-muted-foreground" />
                                      <span className="text-muted-foreground">Cliquez pour uploader un fichier audio (MP3, WAV, OGG)</span>
                                    </>
                                  )}
                                </label>
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Cette musique sera jouée automatiquement sur la page "Le Coffre MSC" pour créer l'ambiance.
                          </p>
                        </div>
                      </div>
                      
                      {/* Combinaison secrète */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Lock className="w-4 h-4" /> Combinaison secrète (24 caractères)
                        </Label>
                        <Input
                          value={annualGameForm.winning_combination}
                          onChange={(e) => setAnnualGameForm({...annualGameForm, winning_combination: e.target.value.toUpperCase().slice(0, 24)})}
                          placeholder="ABCDEFGHIJKLMNOPQRSTUVWX"
                          maxLength={24}
                          className="font-mono text-lg tracking-widest"
                        />
                        <p className="text-sm text-muted-foreground">
                          {annualGameForm.winning_combination?.length || 0}/24 caractères
                        </p>
                      </div>
                      
                      {/* Indices des 12 mois */}
                      <div className="space-y-4">
                        <Label className="flex items-center gap-2 text-lg">
                          <Key className="w-5 h-5" /> Indices des 12 caractères secrets
                        </Label>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {annualGameForm.secret_hints?.map((hint, index) => (
                            <div key={index} className="border rounded-lg p-3 space-y-2">
                              <Label className="text-sm font-semibold text-amber-600">
                                {['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'][hint.month]} - Caractère #{hint.month}
                              </Label>
                              <Input
                                value={hint.hint || ''}
                                onChange={(e) => {
                                  const newHints = [...annualGameForm.secret_hints];
                                  newHints[index] = {...newHints[index], hint: e.target.value};
                                  setAnnualGameForm({...annualGameForm, secret_hints: newHints});
                                }}
                                placeholder="Ex: Sur nos réseaux sociaux..."
                                className="text-sm"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Section Notifications */}
                      <div className="border-t pt-6 mt-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <Bell className="w-5 h-5 text-purple-500" />
                          Notifications par Email
                        </h3>
                        <div className="grid md:grid-cols-2 gap-4">
                          {/* Notifier nouvel indice */}
                          <Card className="border-purple-200 dark:border-purple-800">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium">💡 Nouvel Indice</p>
                                  <p className="text-xs text-muted-foreground">Prévenir les joueurs d'un nouvel indice</p>
                                </div>
                                <Select onValueChange={(month) => handleNotifyNewHint(parseInt(month))}>
                                  <SelectTrigger className="w-32">
                                    <SelectValue placeholder="Mois" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                                      <SelectItem key={m} value={String(m)}>
                                        {['','Jan','Fév','Mar','Avr','Mai','Juin','Juil','Août','Sep','Oct','Nov','Déc'][m]}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </CardContent>
                          </Card>
                          
                          {/* Notifier nouveau quiz */}
                          <Card className="border-purple-200 dark:border-purple-800">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium">🎮 Nouveau Quiz</p>
                                  <p className="text-xs text-muted-foreground">Prévenir d'un quiz mensuel ouvert</p>
                                </div>
                                <Select onValueChange={(quizId) => handleNotifyNewQuiz(quizId)}>
                                  <SelectTrigger className="w-32">
                                    <SelectValue placeholder="Quiz" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {periodicGames.filter(g => g.year === annualYear).map(q => (
                                      <SelectItem key={q.id} value={q.id}>
                                        {['','Jan','Fév','Mar','Avr','Mai','Juin','Juil','Août','Sep','Oct','Nov','Déc'][q.month]}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                        
                        {sendingNotification && (
                          <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                            <span className="text-sm text-purple-600">Envoi des notifications en cours...</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-4 pt-4">
                        <Button 
                          onClick={() => handleSaveAnnualGame()}
                          className="bg-gradient-to-r from-amber-500 to-orange-600"
                        >
                          <Save className="w-4 h-4 mr-2" /> Enregistrer les modifications
                        </Button>
                        <Button variant="outline" onClick={() => window.open('/jeux-concours', '_blank')}>
                          <ExternalLink className="w-4 h-4 mr-2" /> Voir la page publique
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <img src="/images/coffre-fort.png" alt="" className="w-24 h-24 mx-auto mb-4 opacity-50" />
                      <p className="text-muted-foreground mb-4">Aucun Coffre MSC configuré pour {annualYear}</p>
                      <Button onClick={() => setAnnualGameDialogOpen(true)} className="bg-gradient-to-r from-amber-500 to-orange-600">
                        <Plus className="w-4 h-4 mr-2" /> Créer Le Coffre MSC {annualYear}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Questions Tab - Quiz Mensuels */}
            <TabsContent value="questions" className="mt-6 space-y-6">
              {/* Liste des quiz mensuels */}
              <Card className="border-amber-200 dark:border-amber-800">
                <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-amber-500" />
                    Quiz Mensuels du Coffre MSC
                  </CardTitle>
                  <CardDescription>
                    Sélectionnez un quiz pour gérer ses questions
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-2 items-center">
                    {periodicGames.filter(g => g.year === annualYear).length > 0 ? (
                      periodicGames.filter(g => g.year === annualYear).sort((a, b) => a.month - b.month).map((game) => {
                        const currentMonth = new Date().getMonth() + 1;
                        const isCurrentMonth = game.month === currentMonth && game.year === new Date().getFullYear();
                        const isSelected = selectedContest?.id === game.id;
                        
                        return (
                          <Button
                            key={game.id}
                            variant={isSelected ? 'default' : 'outline'}
                            className={`
                              ${isSelected ? 'bg-amber-500 hover:bg-amber-600' : ''}
                              ${isCurrentMonth && !isSelected ? 'ring-2 ring-purple-500 ring-offset-2 bg-purple-50 dark:bg-purple-900/30 border-purple-400 scale-110' : ''}
                              ${isCurrentMonth ? 'relative' : ''}
                            `}
                            onClick={() => {
                              setSelectedContest(game);
                              fetchQuestions(game.id);
                            }}
                          >
                            {isCurrentMonth && (
                              <span className="absolute -top-2 -right-2 w-4 h-4 bg-purple-500 rounded-full animate-pulse" />
                            )}
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mr-2 ${isCurrentMonth ? 'bg-purple-500 text-white' : 'bg-amber-100 dark:bg-amber-900 text-amber-600'}`}>
                              {game.month}
                            </span>
                            {['', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'][game.month]}
                            {isCurrentMonth && <Badge className="ml-2 bg-purple-600 text-xs animate-pulse">EN COURS</Badge>}
                            {!isCurrentMonth && game.is_active && <Badge className="ml-2 bg-green-500 text-xs">Actif</Badge>}
                          </Button>
                        );
                      })
                    ) : (
                      <p className="text-muted-foreground">Aucun quiz mensuel pour {annualYear}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Éditeur de questions pour le quiz sélectionné */}
              {selectedContest && selectedContest.game_type === 'periodique' && (
                <Card className="border-amber-200 dark:border-amber-800">
                  <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Edit className="w-5 h-5 text-amber-500" />
                          Questions : {selectedContest.title}
                        </CardTitle>
                        <CardDescription>
                          {questions.length} question(s) - {['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'][selectedContest.month]} {selectedContest.year}
                        </CardDescription>
                      </div>
                      <Dialog open={questionDialogOpen} onOpenChange={(open) => {
                        setQuestionDialogOpen(open);
                        if (!open) resetQuestionForm();
                      }}>
                        <DialogTrigger asChild>
                          <Button className="bg-amber-500 hover:bg-amber-600" onClick={() => resetQuestionForm()}>
                            <Plus className="w-4 h-4 mr-2" />
                            Ajouter une question
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>{selectedQuestion ? 'Modifier la question' : 'Nouvelle question'}</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label>Question *</Label>
                              <Textarea
                                value={questionFormData.question_text}
                                onChange={(e) => setQuestionFormData({...questionFormData, question_text: e.target.value})}
                                placeholder="Entrez votre question..."
                                rows={2}
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              {questionFormData.options.map((opt, idx) => (
                                <div key={idx} className="space-y-2">
                                  <Label className={opt.is_correct ? 'text-green-600 font-bold' : ''}>
                                    Option {idx + 1} {opt.is_correct && '✓ (Correcte)'}
                                  </Label>
                                  <div className="flex gap-2">
                                    <Input
                                      value={opt.text}
                                      onChange={(e) => {
                                        const newOpts = [...questionFormData.options];
                                        newOpts[idx] = {...newOpts[idx], text: e.target.value};
                                        setQuestionFormData({...questionFormData, options: newOpts});
                                      }}
                                      placeholder={`Option ${idx + 1}`}
                                    />
                                    <Button
                                      type="button"
                                      variant={opt.is_correct ? 'default' : 'outline'}
                                      size="sm"
                                      onClick={() => {
                                        const newOpts = questionFormData.options.map((o, i) => ({
                                          ...o,
                                          is_correct: i === idx
                                        }));
                                        setQuestionFormData({...questionFormData, options: newOpts});
                                      }}
                                      className={opt.is_correct ? 'bg-green-500 hover:bg-green-600' : ''}
                                    >
                                      ✓
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="space-y-2">
                              <Label>Explication (optionnel)</Label>
                              <Textarea
                                value={questionFormData.explanation}
                                onChange={(e) => setQuestionFormData({...questionFormData, explanation: e.target.value})}
                                placeholder="Explication de la bonne réponse..."
                                rows={2}
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setQuestionDialogOpen(false)}>Annuler</Button>
                            <Button onClick={selectedQuestion ? handleUpdateQuestion : handleCreateQuestion} className="bg-amber-500 hover:bg-amber-600">
                              {selectedQuestion ? 'Enregistrer' : 'Créer'}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    {questions.length === 0 ? (
                      <div className="text-center py-8">
                        <HelpCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                        <p className="text-muted-foreground mb-4">Aucune question pour ce quiz</p>
                        <Button onClick={() => { resetQuestionForm(); setQuestionDialogOpen(true); }} className="bg-amber-500 hover:bg-amber-600">
                          <Plus className="w-4 h-4 mr-2" />
                          Ajouter la première question
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {questions.map((q, idx) => {
                          // Support both formats: question_text and question
                          const questionText = q.question_text || q.question || '';
                          // Support both formats: options as strings or objects with is_correct
                          const hasObjectOptions = q.options?.[0] && typeof q.options[0] === 'object';
                          const correctIndex = hasObjectOptions 
                            ? q.options.findIndex(opt => opt.is_correct) 
                            : q.correct_answer;
                          
                          return (
                            <div key={q.id} className="p-4 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className="font-medium">
                                    <span className="text-amber-500 mr-2">Q{idx + 1}.</span>
                                    {questionText}
                                  </p>
                                  <div className="grid grid-cols-2 gap-2 mt-2">
                                    {q.options?.map((opt, optIdx) => {
                                      const optText = typeof opt === 'object' ? opt.text : opt;
                                      const isCorrect = hasObjectOptions ? opt.is_correct : optIdx === correctIndex;
                                      return (
                                        <div
                                          key={optIdx}
                                          className={`text-sm p-2 rounded ${isCorrect ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 font-medium' : 'bg-slate-100 dark:bg-slate-800'}`}
                                        >
                                          {isCorrect && '✓ '}{optText}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-amber-500 hover:text-amber-700 hover:bg-amber-50"
                                    onClick={() => openEditQuestion(q)}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleDeleteQuestion(q.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            
            {/* Players Tab - Joueurs du Coffre */}
            <TabsContent value="players" className="mt-6">
              <Card className="border-amber-200 dark:border-amber-800">
                <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-amber-500" />
                        Joueurs du Coffre MSC {annualYear}
                      </CardTitle>
                      <CardDescription>
                        {annualLeaderboard.length} joueurs inscrits - Détails des participations et caractères collectés
                      </CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-white hover:bg-green-50 border-green-300 text-green-700 hover:text-green-800"
                      onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/game/cms/players/export?year=${annualYear}`, '_blank')}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Exporter CSV
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  {annualLeaderboard.length > 0 ? (
                    <div className="space-y-4 max-h-[700px] overflow-y-auto">
                      {annualLeaderboard.map((player, index) => (
                        <div key={player.player_id || index} className="p-4 border rounded-xl bg-gradient-to-r from-white to-amber-50/30 hover:shadow-md transition-shadow">
                          {/* Header row */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <span className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                                index === 0 ? 'bg-amber-500 text-white' :
                                index === 1 ? 'bg-slate-400 text-white' :
                                index === 2 ? 'bg-orange-600 text-white' :
                                'bg-slate-100 dark:bg-slate-800'
                              }`}>
                                {index + 1}
                              </span>
                              {player.avatar_url ? (
                                <img src={player.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-amber-400" />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-lg">
                                  {player.display_name?.[0]?.toUpperCase() || '?'}
                                </div>
                              )}
                              <div>
                                <p className="font-semibold text-lg">{player.display_name || 'Anonyme'}</p>
                                <p className="text-sm text-muted-foreground">{player.email}</p>
                                <p className="text-xs text-muted-foreground">{player.company_name}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <p className="text-2xl font-bold text-amber-600">{player.total_score || 0} pts</p>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeletePlayer(player.player_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Stats row */}
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                            <div className="bg-blue-50 rounded-lg p-2 text-center">
                              <p className="text-xl font-bold text-blue-600">{player.games_played || 0}</p>
                              <p className="text-xs text-blue-600/70">Participations</p>
                            </div>
                            <div className="bg-green-50 rounded-lg p-2 text-center">
                              <p className="text-xl font-bold text-green-600">{player.total_correct || 0}/{player.total_questions || 0}</p>
                              <p className="text-xs text-green-600/70">Bonnes rép.</p>
                            </div>
                            <div className="bg-purple-50 rounded-lg p-2 text-center">
                              <p className="text-xl font-bold text-purple-600">{player.success_rate || 0}%</p>
                              <p className="text-xs text-purple-600/70">Réussite</p>
                            </div>
                            <div className="bg-red-50 rounded-lg p-2 text-center">
                              <p className="text-xl font-bold text-red-600">{player.completed_months?.length || 0}</p>
                              <p className="text-xs text-red-600/70">Quiz validés</p>
                            </div>
                            <div className="bg-amber-50 rounded-lg p-2 text-center">
                              <p className="text-xl font-bold text-amber-600">{player.characters_collected || 0}/24</p>
                              <p className="text-xs text-amber-600/70">Caractères</p>
                            </div>
                          </div>
                          
                          {/* Characters discovered */}
                          {player.discovered_chars && player.discovered_chars.length > 0 && (
                            <div className="bg-slate-100 rounded-lg p-3">
                              <p className="text-xs font-medium text-slate-600 mb-2">📜 Caractères découverts :</p>
                              <div className="flex flex-wrap gap-1 mb-2">
                                {player.discovered_chars.map((char, idx) => (
                                  <span 
                                    key={idx}
                                    className={`inline-flex items-center px-2 py-1 rounded text-xs font-mono font-bold ${
                                      char.type === 'quiz' 
                                        ? 'bg-red-500 text-white' 
                                        : 'bg-white text-slate-800 border border-slate-300'
                                    }`}
                                    title={`${char.type === 'quiz' ? 'Quiz' : 'Secret'} - Position ${char.position}`}
                                  >
                                    {char.character}
                                    <span className="ml-1 text-[10px] opacity-70">
                                      ({['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Août','Sep','Oct','Nov','Déc'][char.month - 1]})
                                    </span>
                                  </span>
                                ))}
                              </div>
                              {/* Combination progress */}
                              <div className="pt-2 border-t border-slate-200">
                                <p className="text-xs text-slate-500 mb-1">Combinaison :</p>
                                <div className="flex flex-wrap gap-0.5">
                                  {Array.from({length: 24}).map((_, i) => {
                                    const char = player.combination?.[i];
                                    const isQuiz = i % 2 === 0;
                                    const hasChar = char && char !== '_';
                                    return (
                                      <span 
                                        key={i}
                                        className={`w-5 h-6 flex items-center justify-center text-xs font-mono rounded ${
                                          hasChar 
                                            ? isQuiz 
                                              ? 'bg-red-500 text-white font-bold' 
                                              : 'bg-white text-slate-800 border border-slate-400 font-bold'
                                            : 'bg-slate-200 text-slate-400'
                                        }`}
                                      >
                                        {hasChar ? char : '?'}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* No characters */}
                          {(!player.discovered_chars || player.discovered_chars.length === 0) && (
                            <div className="bg-slate-50 rounded-lg p-2 text-center">
                              <p className="text-sm text-slate-400">Aucun caractère découvert</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                      <p className="text-muted-foreground">Aucun joueur inscrit pour le moment</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Rules Tab */}
            <TabsContent value="rules" className="mt-6">
              <Card className="border-amber-200 dark:border-amber-800">
                <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-amber-500" />
                    Règlement du Coffre MSC
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <Textarea
                    value={rules}
                    onChange={(e) => setRules(e.target.value)}
                    placeholder="Rédigez le règlement complet du jeu Le Coffre MSC..."
                    rows={15}
                    className="font-mono text-sm"
                  />
                  <div className="flex gap-4 mt-4">
                    <Button 
                      onClick={handleSaveRules}
                      className="bg-gradient-to-r from-amber-500 to-orange-600"
                    >
                      <Save className="w-4 h-4 mr-2" /> Enregistrer
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Winners Tab */}
            <TabsContent value="winners" className="mt-6">
              <Card className="border-amber-200 dark:border-amber-800">
                <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Gagnant du Coffre MSC {annualYear}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {annualGame?.winner_id ? (
                    <div className="text-center py-8 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 rounded-xl">
                      <Trophy className="w-20 h-20 mx-auto text-amber-500 mb-4" />
                      <h3 className="text-2xl font-bold mb-2">🎉 Félicitations !</h3>
                      <p className="text-xl font-semibold text-amber-600">{annualGame.winner_pseudo || annualGame.winner_name}</p>
                      <p className="text-muted-foreground mt-2">
                        A ouvert le coffre le {annualGame.won_at ? new Date(annualGame.won_at).toLocaleDateString('fr-FR') : '24 décembre'}
                      </p>
                      <p className="mt-4 text-lg">Lot remporté : <strong>{annualGame.prize}</strong></p>
                      
                      {/* Bouton pour envoyer l'email au gagnant */}
                      <div className="mt-6">
                        <Button 
                          onClick={() => handleNotifyCoffreWinner(annualGame.winner_id)}
                          disabled={sendingNotification}
                          className="bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700"
                        >
                          {sendingNotification ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi en cours...</>
                          ) : (
                            <><Mail className="w-4 h-4 mr-2" /> Envoyer l'email de félicitations</>
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Lock className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                      <p className="text-lg mb-2">Le coffre n&apos;a pas encore été ouvert</p>
                      <p className="text-muted-foreground">Le premier joueur avec la bonne combinaison le 24 décembre {annualYear} gagnera !</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Results Tab */}
            <TabsContent value="results" className="mt-6">
              <Card className="border-amber-200 dark:border-amber-800">
                <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-amber-500" />
                    Page de Résultats
                  </CardTitle>
                  <CardDescription>
                    Lien vers la page publique des résultats
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <p className="text-muted-foreground">
                      La page de résultats affiche le classement des joueurs et le gagnant du Coffre MSC.
                    </p>
                    <div className="flex gap-4">
                      <Button variant="outline" onClick={() => window.open('/jeux-concours', '_blank')}>
                        <ExternalLink className="w-4 h-4 mr-2" /> Voir la page Jeux Concours
                      </Button>
                      <Button variant="outline" onClick={() => window.open('/mon-espace-joueur', '_blank')}>
                        <ExternalLink className="w-4 h-4 mr-2" /> Voir l&apos;espace joueur
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* ============================================ */}
      {/* SECTION: JEUX PONCTUELS */}
      {/* ============================================ */}
      {mainSection === 'ponctuel' && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetForm} className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau jeu ponctuel
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-cyan-500" />
                    {selectedContest ? 'Modifier le jeu' : 'Créer un jeu ponctuel'}
                  </DialogTitle>
                </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>Titre du jeu *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: Quiz Marketing Digital"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Décrivez le jeu concours..."
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Lot à gagner *</Label>
                  <Input
                    value={formData.prize}
                    onChange={(e) => setFormData({ ...formData, prize: e.target.value })}
                    placeholder="Ex: iPhone 15 Pro"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Valeur du lot (€HT)</Label>
                  <Input
                    type="number"
                    value={formData.prize_value}
                    onChange={(e) => setFormData({ ...formData, prize_value: e.target.value })}
                    placeholder="Ex: 999"
                    min={0}
                    step={0.01}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description du lot</Label>
                  <Input
                    value={formData.prize_description}
                    onChange={(e) => setFormData({ ...formData, prize_description: e.target.value })}
                    placeholder="Détails supplémentaires..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de début</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de fin</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Temps par question (secondes)</Label>
                  <Input
                    type="number"
                    value={formData.time_limit_seconds}
                    onChange={(e) => setFormData({ ...formData, time_limit_seconds: parseInt(e.target.value) || 60 })}
                    min={10}
                    max={300}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Points par bonne réponse</Label>
                  <Input
                    type="number"
                    value={formData.points_per_correct}
                    onChange={(e) => setFormData({ ...formData, points_per_correct: parseInt(e.target.value) || 10 })}
                    min={1}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Taux de réussite minimum (%)</Label>
                  <Input
                    type="number"
                    value={formData.min_success_rate}
                    onChange={(e) => setFormData({ ...formData, min_success_rate: Math.min(100, Math.max(0, parseInt(e.target.value) || 100)) })}
                    min={0}
                    max={100}
                  />
                  <p className="text-xs text-muted-foreground">Pourcentage minimum de bonnes réponses pour valider le quiz (ex: 70 pour 70%)</p>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={formData.time_bonus_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, time_bonus_enabled: checked })}
                  />
                  <Label className="cursor-pointer">Bonus de rapidité</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={formData.is_main_game}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_main_game: checked })}
                  />
                  <div>
                    <Label className="cursor-pointer flex items-center gap-1">
                      <Crown className="w-4 h-4 text-amber-500" />
                      Jeu Annuel
                    </Label>
                    <p className="text-xs text-muted-foreground">Mis en avant sur la page d&apos;accueil</p>
                  </div>
                </div>
              </div>
              
              {/* Game Type Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-purple-500" />
                  Type de jeu
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select
                      value={formData.game_type}
                      onValueChange={(v) => setFormData({ 
                        ...formData, 
                        game_type: v, 
                        month: v === 'ponctuel' ? 0 : formData.month,
                        rattrapage_for_month: v === 'rattrapage' ? (formData.rattrapage_for_month || 1) : 0
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ponctuel">🎯 Ponctuel</SelectItem>
                        <SelectItem value="periodique">📅 Périodique (mensuel)</SelectItem>
                        <SelectItem value="rattrapage">🔄 Rattrapage</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Mode de récompense</Label>
                    <Select
                      value={formData.reward_mode}
                      onValueChange={(v) => setFormData({ ...formData, reward_mode: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="classement">🏆 Classement</SelectItem>
                        <SelectItem value="tirage_au_sort">🎲 Tirage au sort</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Paramètres du tirage au sort */}
                  {formData.reward_mode === 'tirage_au_sort' && (
                    <div className="col-span-2 p-4 bg-purple-50 dark:bg-purple-950/20 rounded-xl space-y-4 border border-purple-200 dark:border-purple-800">
                      <h4 className="font-semibold text-purple-700 dark:text-purple-300 flex items-center gap-2">
                        <Shuffle className="w-4 h-4" /> Configuration du tirage au sort
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>📅 Date et heure du tirage</Label>
                          <Input
                            type="datetime-local"
                            value={formData.draw_datetime || ''}
                            onChange={(e) => setFormData({ ...formData, draw_datetime: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>📍 Lieu du tirage</Label>
                          <Input
                            value={formData.draw_location || ''}
                            onChange={(e) => setFormData({ ...formData, draw_location: e.target.value })}
                            placeholder="Ex: Sur notre site, En live Facebook..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>🔗 URL des résultats</Label>
                          <Input
                            value={formData.draw_results_url || ''}
                            onChange={(e) => setFormData({ ...formData, draw_results_url: e.target.value })}
                            placeholder="Ex: https://agence-msc.fr/jeux-concours"
                          />
                        </div>
                        <div className="space-y-2 col-span-2">
                          <Label>💬 Message personnalisé (rappels)</Label>
                          <Input
                            value={formData.draw_reminder_message || ''}
                            onChange={(e) => setFormData({ ...formData, draw_reminder_message: e.target.value })}
                            placeholder="Message additionnel pour les emails de rappel..."
                          />
                        </div>
                      </div>
                      <p className="text-xs text-purple-600 dark:text-purple-400">
                        📧 Les rappels automatiques seront envoyés J-7, J-1 et le jour J du tirage
                      </p>
                    </div>
                  )}
                  
                  {formData.game_type === 'periodique' && (
                    <>
                      <div className="space-y-2">
                        <Label>Année</Label>
                        <Select
                          value={String(formData.year)}
                          onValueChange={(v) => setFormData({ ...formData, year: parseInt(v) })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2024">2024</SelectItem>
                            <SelectItem value="2025">2025</SelectItem>
                            <SelectItem value="2026">2026</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Mois</Label>
                        <Select
                          value={String(formData.month)}
                          onValueChange={(v) => setFormData({ ...formData, month: parseInt(v) })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Janvier</SelectItem>
                            <SelectItem value="2">Février</SelectItem>
                            <SelectItem value="3">Mars</SelectItem>
                            <SelectItem value="4">Avril</SelectItem>
                            <SelectItem value="5">Mai</SelectItem>
                            <SelectItem value="6">Juin</SelectItem>
                            <SelectItem value="7">Juillet</SelectItem>
                            <SelectItem value="8">Août</SelectItem>
                            <SelectItem value="9">Septembre</SelectItem>
                            <SelectItem value="10">Octobre</SelectItem>
                            <SelectItem value="11">Novembre</SelectItem>
                            <SelectItem value="12">Décembre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Caractère de récompense (Coffre)</Label>
                        <Input
                          value={formData.reward_character}
                          onChange={(e) => setFormData({ ...formData, reward_character: e.target.value.slice(0, 1).toUpperCase() })}
                          placeholder="Ex: M, S, C..."
                          maxLength={1}
                          className="text-center text-2xl font-bold uppercase"
                        />
                        <p className="text-xs text-muted-foreground">Le caractère que le joueur gagnera pour la combinaison du coffre (1 seul caractère)</p>
                      </div>
                    </>
                  )}
                  {formData.game_type === 'rattrapage' && (
                    <>
                      <div className="space-y-2">
                        <Label>Année du jeu annuel</Label>
                        <Select
                          value={String(formData.year)}
                          onValueChange={(v) => setFormData({ ...formData, year: parseInt(v) })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2024">2024</SelectItem>
                            <SelectItem value="2025">2025</SelectItem>
                            <SelectItem value="2026">2026</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Mois à rattraper</Label>
                        <Select
                          value={String(formData.rattrapage_for_month)}
                          onValueChange={(v) => setFormData({ ...formData, rattrapage_for_month: parseInt(v) })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Janvier</SelectItem>
                            <SelectItem value="2">Février</SelectItem>
                            <SelectItem value="3">Mars</SelectItem>
                            <SelectItem value="4">Avril</SelectItem>
                            <SelectItem value="5">Mai</SelectItem>
                            <SelectItem value="6">Juin</SelectItem>
                            <SelectItem value="7">Juillet</SelectItem>
                            <SelectItem value="8">Août</SelectItem>
                            <SelectItem value="9">Septembre</SelectItem>
                            <SelectItem value="10">Octobre</SelectItem>
                            <SelectItem value="11">Novembre</SelectItem>
                            <SelectItem value="12">Décembre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                </div>
                {formData.game_type === 'periodique' && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Les jeux périodiques contribuent au classement annuel cumulé.
                  </p>
                )}
                {formData.game_type === 'rattrapage' && (
                  <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-sm text-amber-800">
                      <strong>🔄 Quiz de rattrapage</strong> : Permet aux joueurs qui n&apos;ont pas obtenu 10/10 sur le quiz du mois sélectionné de retenter leur chance et gagner le caractère manquant pour Le Coffre MSC.
                    </p>
                  </div>
                )}
              </div>
              
              {/* Partnership Section */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-3 mb-4">
                  <Switch
                    checked={formData.is_partnership}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_partnership: checked })}
                  />
                  <div>
                    <Label className="cursor-pointer flex items-center gap-1">
                      <Handshake className="w-4 h-4 text-blue-500" />
                      Jeu en partenariat
                    </Label>
                    <p className="text-xs text-muted-foreground">Affiche le logo et les infos du partenaire</p>
                  </div>
                </div>
                
                {formData.is_partnership && (
                  <div className="grid grid-cols-2 gap-4 bg-blue-50 p-4 rounded-lg">
                    <div className="space-y-2">
                      <Label>Nom du partenaire *</Label>
                      <Input
                        value={formData.partner_name}
                        onChange={(e) => setFormData({ ...formData, partner_name: e.target.value })}
                        placeholder="Ex: Entreprise XYZ"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Logo du partenaire (URL)</Label>
                      <Input
                        value={formData.partner_logo_url}
                        onChange={(e) => setFormData({ ...formData, partner_logo_url: e.target.value })}
                        placeholder="https://..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Site web / Réseau social</Label>
                      <Input
                        value={formData.partner_website}
                        onChange={(e) => setFormData({ ...formData, partner_website: e.target.value })}
                        placeholder="https://..."
                      />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Présentation du partenaire</Label>
                      <Textarea
                        value={formData.partner_description}
                        onChange={(e) => setFormData({ ...formData, partner_description: e.target.value })}
                        placeholder="Quelques mots sur le partenaire..."
                        rows={2}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Video & Site Quiz Section */}
              <div className="border-t pt-4 mt-4">
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Youtube className="w-4 h-4 text-red-500" />
                      Vidéo de présentation (YouTube)
                    </Label>
                    <Input
                      value={formData.youtube_video_url}
                      onChange={(e) => setFormData({ ...formData, youtube_video_url: e.target.value })}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={formData.is_site_quiz}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_site_quiz: checked })}
                    />
                    <div>
                      <Label className="cursor-pointer flex items-center gap-1">
                        <Building className="w-4 h-4 text-cyan-500" />
                        Quiz sur nos services
                      </Label>
                      <p className="text-xs text-muted-foreground">Les questions portent sur le site et nos services (avec liens)</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                <Button
                  onClick={selectedContest ? handleUpdateContest : handleCreateContest}
                  className="bg-gradient-to-r from-amber-500 to-orange-600"
                >
                  {selectedContest ? 'Enregistrer' : 'Créer le jeu'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 bg-cyan-50 dark:bg-cyan-900/20">
          <TabsTrigger value="contests" className="flex items-center gap-2 data-[state=active]:bg-cyan-500 data-[state=active]:text-white">
            <Trophy className="w-4 h-4" />
            Jeux ({contests.filter(c => c.game_type !== 'periodique').length})
          </TabsTrigger>
          <TabsTrigger value="questions" className="flex items-center gap-2 data-[state=active]:bg-cyan-500 data-[state=active]:text-white">
            <HelpCircle className="w-4 h-4" />
            Questions
          </TabsTrigger>
          <TabsTrigger value="players" className="flex items-center gap-2 data-[state=active]:bg-cyan-500 data-[state=active]:text-white">
            <Users className="w-4 h-4" />
            Joueurs ({players.length})
          </TabsTrigger>
          <TabsTrigger value="winners" className="flex items-center gap-2 data-[state=active]:bg-cyan-500 data-[state=active]:text-white">
            <Crown className="w-4 h-4" />
            Gagnants
          </TabsTrigger>
          <TabsTrigger value="rules" className="flex items-center gap-2 data-[state=active]:bg-cyan-500 data-[state=active]:text-white">
            <FileText className="w-4 h-4" />
            Règlement
          </TabsTrigger>
        </TabsList>

        {/* Contests Tab */}
        <TabsContent value="contests" className="space-y-4">
          {contests.filter(c => c.game_type !== 'periodique').length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Trophy className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">Aucun jeu ponctuel pour le moment</p>
                <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer votre premier jeu
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Jeu</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Lot</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Type</th>
                        <th className="text-center py-3 px-4 font-medium text-muted-foreground">Statut</th>
                        <th className="text-center py-3 px-4 font-medium text-muted-foreground">Date fin</th>
                        <th className="text-right py-3 px-4 font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {contests
                        .filter(c => c.game_type !== 'periodique')
                        .sort((a, b) => {
                          if (a.is_finished && !b.is_finished) return 1;
                          if (!a.is_finished && b.is_finished) return -1;
                          return 0;
                        })
                        .map((contest) => (
                        <tr 
                          key={contest.id} 
                          className={`
                            hover:bg-slate-50 transition-colors
                            ${contest.is_finished ? 'opacity-50 bg-slate-50/50' : ''} 
                            ${contest.is_main_game ? 'bg-amber-50/30' : ''}
                          `}
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {contest.game_type === 'periodique' && (
                                <div className="w-1 h-8 bg-purple-500 rounded-full" title="Jeu périodique" />
                              )}
                              {contest.game_type === 'rattrapage' && (
                                <div className="w-1 h-8 bg-amber-500 rounded-full" title="Quiz de rattrapage" />
                              )}
                              {contest.is_partnership && (
                                <div className="w-1 h-8 bg-blue-500 rounded-full" title="Partenariat" />
                              )}
                              <div>
                                <div className="font-medium flex items-center gap-2">
                                  {contest.title}
                                  {contest.is_main_game && <Crown className="w-4 h-4 text-amber-500" />}
                                </div>
                                <div className="flex gap-1 mt-1 flex-wrap">
                                  {contest.is_partnership && (
                                    <Badge variant="outline" className="text-xs h-5 border-blue-300 text-blue-600">
                                      <Handshake className="w-3 h-3 mr-1" />
                                      {contest.partner_name || 'Partenariat'}
                                    </Badge>
                                  )}
                                  {contest.is_site_quiz && (
                                    <Badge variant="outline" className="text-xs h-5 border-cyan-300 text-cyan-600">
                                      Quiz MSC
                                    </Badge>
                                  )}
                                  {contest.game_type === 'periodique' && (
                                    <Badge variant="outline" className="text-xs h-5 border-purple-300 text-purple-600">
                                      {['', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'][contest.month]} {contest.year}
                                    </Badge>
                                  )}
                                  {contest.game_type === 'rattrapage' && (
                                    <Badge variant="outline" className="text-xs h-5 border-amber-300 text-amber-600 bg-amber-50">
                                      🔄 Rattrapage {['', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'][contest.rattrapage_for_month]} {contest.year}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div>
                              <span className="text-amber-600 font-medium">{contest.prize}</span>
                              {contest.prize_value && (
                                <p className="text-xs text-slate-500">{contest.prize_value} €HT</p>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            {contest.reward_mode === 'tirage_au_sort' ? (
                              <Badge variant="outline" className="text-xs border-pink-300 text-pink-500">
                                <Shuffle className="w-3 h-3 mr-1" />
                                Tirage
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">Classement</Badge>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {contest.is_finished ? (
                              <Badge variant="outline" className="text-xs text-slate-500">
                                <Flag className="w-3 h-3 mr-1" />
                                Terminé
                              </Badge>
                            ) : contest.is_active ? (
                              <Badge className="text-xs bg-green-500">
                                <Play className="w-3 h-3 mr-1" />
                                Actif
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">
                                <Pause className="w-3 h-3 mr-1" />
                                Inactif
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center text-muted-foreground">
                            {contest.end_date ? new Date(contest.end_date).toLocaleDateString('fr-FR') : '-'}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyGameUrl(contest)}
                                title="Copier l'URL"
                                className="h-8 w-8 p-0"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(`/jeu/${contest.id}`, '_blank')}
                                title="Voir le jeu"
                                className="h-8 w-8 p-0"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => selectContestForQuestions(contest)}
                                title="Questions"
                                className="h-8 w-8 p-0"
                              >
                                <HelpCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditContest(contest)}
                                title="Modifier"
                                className="h-8 w-8 p-0"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {!contest.is_finished && contest.is_active && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-purple-600 hover:text-purple-700"
                                    onClick={() => handleNotifyNewContest(contest.id)}
                                    disabled={sendingNotification}
                                    title="Notifier les joueurs"
                                  >
                                    <Bell className="w-4 h-4" />
                                  </Button>
                                  {contest.reward_mode === 'tirage_au_sort' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-pink-600 hover:text-pink-700"
                                      onClick={() => openDrawModal(contest)}
                                      title="Lancer le tirage au sort"
                                    >
                                      <Shuffle className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-orange-600 hover:text-orange-700"
                                    onClick={() => handleFinishContest(contest.id)}
                                    title="Terminer le jeu"
                                  >
                                    <Flag className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                              {contest.is_finished && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700"
                                  onClick={() => handleNotifyGameResults(contest.id)}
                                  disabled={sendingNotification}
                                  title="Envoyer les résultats"
                                >
                                  <Mail className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                                onClick={() => handleDeleteContest(contest.id)}
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Questions Tab */}
        <TabsContent value="questions" className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                Questions pour :
              </h2>
              <Select 
                value={selectedContest?.id || ''} 
                onValueChange={(v) => {
                  const contest = contests.find(c => c.id === v);
                  if (contest) {
                    setSelectedContest(contest);
                    fetchQuestions(contest.id);
                  }
                }}
              >
                <SelectTrigger className="w-[350px]">
                  <SelectValue placeholder="Sélectionner un jeu">
                    {selectedContest?.title || 'Sélectionner un jeu'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {contests.filter(c => c.game_type !== 'periodique').map((contest) => (
                    <SelectItem key={contest.id} value={contest.id}>
                      {contest.title} {contest.month ? `(Mois ${contest.month})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {selectedContest && (
            <div className="flex justify-end gap-2 mb-4">
              <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-purple-500 text-purple-600 hover:bg-purple-50">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Générer avec IA
                  </Button>
                </DialogTrigger>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-purple-500" />
                          Générer des questions avec l&apos;IA
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div className="col-span-2 space-y-2">
                            <Label>Sujet / Thème *</Label>
                            <Input
                              value={aiTopic}
                              onChange={(e) => setAiTopic(e.target.value)}
                              placeholder="Ex: Histoire de France, Marketing Digital, Culture Générale..."
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Nombre de questions</Label>
                            <Select value={String(aiCount)} onValueChange={(v) => setAiCount(parseInt(v))}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="3">3 questions</SelectItem>
                                <SelectItem value="5">5 questions</SelectItem>
                                <SelectItem value="10">10 questions</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Difficulté</Label>
                          <Select value={aiDifficulty} onValueChange={setAiDifficulty}>
                            <SelectTrigger className="w-[200px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="easy">🟢 Facile</SelectItem>
                              <SelectItem value="medium">🟡 Moyen</SelectItem>
                              <SelectItem value="hard">🔴 Difficile</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          onClick={handleGenerateQuestions}
                          disabled={aiLoading || !aiTopic.trim()}
                          className="w-full bg-gradient-to-r from-purple-500 to-indigo-600"
                        >
                          {aiLoading ? (
                            <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white mr-2" /> Génération en cours...</>
                          ) : (
                            <><Sparkles className="w-4 h-4 mr-2" /> Générer les questions</>
                          )}
                        </Button>

                        {generatedQuestions.length > 0 && (
                          <div className="space-y-3 pt-4 border-t">
                            <h3 className="font-semibold">Questions générées ({generatedQuestions.length})</h3>
                            {generatedQuestions.map((q, idx) => (
                              <Card key={idx} className="bg-slate-50">
                                <CardContent className="p-4">
                                  <p className="font-medium mb-2">{q.question_text}</p>
                                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                                    {q.options?.map((opt, optIdx) => (
                                      <div
                                        key={optIdx}
                                        className={`p-2 rounded border ${opt.is_correct ? 'bg-green-100 border-green-300' : 'bg-white'}`}
                                      >
                                        {opt.text} {opt.is_correct && <CheckCircle className="w-3 h-3 inline ml-1 text-green-600" />}
                                      </div>
                                    ))}
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => handleAddGeneratedQuestion(q)}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Ajouter au jeu
                                  </Button>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={questionDialogOpen} onOpenChange={setQuestionDialogOpen}>
                    <DialogTrigger asChild>
                      <Button onClick={resetQuestionForm}>
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvelle question
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>
                          {selectedQuestion ? 'Modifier la question' : 'Créer une question'}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Question *</Label>
                          <Textarea
                            value={questionFormData.question_text}
                            onChange={(e) => setQuestionFormData({ ...questionFormData, question_text: e.target.value })}
                            placeholder="Posez votre question..."
                            rows={3}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Type</Label>
                            <Select
                              value={questionFormData.question_type}
                              onValueChange={(v) => setQuestionFormData({ ...questionFormData, question_type: v })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="text">Texte</SelectItem>
                                <SelectItem value="image">Image</SelectItem>
                                <SelectItem value="audio">Audio</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Points</Label>
                            <Input
                              type="number"
                              value={questionFormData.points}
                              onChange={(e) => setQuestionFormData({ ...questionFormData, points: parseInt(e.target.value) || 10 })}
                              min={1}
                            />
                          </div>
                        </div>
                        {(questionFormData.question_type === 'image' || questionFormData.question_type === 'audio') && (
                          <div className="space-y-2">
                            <Label>URL du média</Label>
                            <Input
                              value={questionFormData.media_url}
                              onChange={(e) => setQuestionFormData({ ...questionFormData, media_url: e.target.value })}
                              placeholder="https://..."
                            />
                          </div>
                        )}
                        <div className="space-y-3">
                          <Label>Options de réponse (cochez la bonne réponse)</Label>
                          {questionFormData.options.map((opt, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="correct_answer"
                                checked={opt.is_correct}
                                onChange={() => {
                                  const newOptions = questionFormData.options.map((o, i) => ({
                                    ...o,
                                    is_correct: i === idx
                                  }));
                                  setQuestionFormData({ ...questionFormData, options: newOptions });
                                }}
                                className="w-4 h-4 text-green-600"
                              />
                              <Input
                                value={opt.text}
                                onChange={(e) => {
                                  const newOptions = [...questionFormData.options];
                                  newOptions[idx].text = e.target.value;
                                  setQuestionFormData({ ...questionFormData, options: newOptions });
                                }}
                                placeholder={`Option ${idx + 1}`}
                                className={opt.is_correct ? 'border-green-500' : ''}
                              />
                            </div>
                          ))}
                        </div>
                        
                        {/* Site Link for "Connaissez-vous MSC" quiz */}
                        {selectedContest?.is_site_quiz && (
                          <div className="space-y-2 p-4 rounded-lg bg-cyan-50 border border-cyan-200">
                            <Label className="flex items-center gap-2 text-cyan-700">
                              <Link className="w-4 h-4" />
                              Lien vers la page du site (Quiz MSC)
                            </Label>
                            <Input
                              value={questionFormData.page_link || ''}
                              onChange={(e) => setQuestionFormData({ ...questionFormData, page_link: e.target.value })}
                              placeholder="Ex: /site/services ou https://msc-agence.fr/services"
                            />
                            <Input
                              value={questionFormData.page_link_text || ''}
                              onChange={(e) => setQuestionFormData({ ...questionFormData, page_link_text: e.target.value })}
                              placeholder="Texte du lien (ex: Découvrir nos services)"
                              className="mt-2"
                            />
                            <p className="text-xs text-cyan-600">
                              Ce lien sera affiché après la réponse pour renvoyer vers la page pertinente du site MSC.
                            </p>
                          </div>
                        )}
                        <div className="flex justify-end gap-3 pt-4">
                          <Button variant="outline" onClick={() => setQuestionDialogOpen(false)}>Annuler</Button>
                          <Button onClick={selectedQuestion ? handleUpdateQuestion : handleCreateQuestion}>
                            {selectedQuestion ? 'Enregistrer' : 'Créer'}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
          )}

          {selectedContest && (
            <>
              {questions.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <HelpCircle className="w-12 h-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">Aucune question pour ce jeu</p>
                    <div className="flex gap-2">
                      <Button onClick={() => { resetQuestionForm(); setQuestionDialogOpen(true); }}>
                        <Plus className="w-4 h-4 mr-2" />
                        Créer manuellement
                      </Button>
                      <Button variant="outline" onClick={() => setAiDialogOpen(true)}>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Générer avec IA
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {questions.map((question, idx) => (
                    <Card key={question.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline">Q{idx + 1}</Badge>
                              <Badge>{question.points} pts</Badge>
                            </div>
                            <p className="font-medium mb-2">{question.question_text}</p>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              {question.options?.map((opt, optIdx) => (
                                <div
                                  key={optIdx}
                                  className={`p-2 rounded border ${opt.is_correct ? 'bg-green-50 border-green-300 text-green-700' : 'bg-slate-50'}`}
                                >
                                  {opt.text}
                                  {opt.is_correct && <CheckCircle className="w-3 h-3 inline ml-1" />}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditQuestion(question)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500"
                              onClick={() => handleDeleteQuestion(question.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              </>
            )}
            {!selectedContest && (
              <Card className="border-dashed mt-4">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <HelpCircle className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Sélectionnez un jeu dans la liste ci-dessus pour voir ou gérer ses questions</p>
                </CardContent>
              </Card>
            )}
        </TabsContent>

        {/* Players Tab */}
        <TabsContent value="players" className="space-y-6">
          {/* Sub-tabs for players */}
          <div className="flex flex-wrap gap-2 p-1 bg-muted rounded-lg">
            <button
              onClick={() => setPlayersSubTab('pending')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                playersSubTab === 'pending' 
                  ? 'bg-amber-500 text-white shadow-sm' 
                  : 'hover:bg-background text-muted-foreground'
              }`}
            >
              <AlertCircle className="w-4 h-4" />
              En attente ({pendingPlayers.length})
            </button>
            <button
              onClick={() => setPlayersSubTab('pseudo')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                playersSubTab === 'pseudo' 
                  ? 'bg-purple-500 text-white shadow-sm' 
                  : 'hover:bg-background text-muted-foreground'
              }`}
            >
              <AtSign className="w-4 h-4" />
              Pseudos à valider ({validatedPlayers.filter(p => p.pseudo && !p.pseudo_validated).length})
            </button>
            <button
              onClick={() => setPlayersSubTab('validated')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                playersSubTab === 'validated' 
                  ? 'bg-green-500 text-white shadow-sm' 
                  : 'hover:bg-background text-muted-foreground'
              }`}
            >
              <CheckCircle className="w-4 h-4" />
              Joueurs validés ({validatedPlayers.length})
            </button>
          </div>

          {/* Export buttons */}
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/game/cms/players/export`, '_blank')}
            >
              <Download className="w-4 h-4 mr-2" />
              Exporter tous les joueurs (CSV)
            </Button>
          </div>

          {/* Pending Players Sub-tab */}
          {playersSubTab === 'pending' && (
            <Card className="border-amber-300 bg-amber-50/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  Joueurs en attente de validation ({pendingPlayers.length})
                </CardTitle>
                <p className="text-sm text-muted-foreground">Ces joueurs ont créé un compte et attendent votre validation pour participer aux jeux.</p>
              </CardHeader>
              <CardContent>
                {pendingPlayers.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <p className="text-muted-foreground">Aucun joueur en attente de validation</p>
                    <p className="text-sm text-muted-foreground mt-1">Tous les joueurs ont été traités !</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingPlayers.map((player) => (
                      <div key={player.id} className="flex items-center justify-between p-4 bg-white rounded-lg border shadow-sm">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-lg">
                            {player.first_name?.[0]?.toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-lg">{player.first_name}</p>
                              {player.pseudo && (
                                <Badge variant="outline" className="text-purple-600 border-purple-300">
                                  <AtSign className="w-3 h-3 mr-1" />
                                  {player.pseudo}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{player.email}</p>
                            <p className="text-sm text-muted-foreground">{player.company_name} • {player.phone}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              📅 Inscrit le {new Date(player.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleValidatePlayer(player.id)}>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Valider
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDeletePlayer(player.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Pseudo Validation Sub-tab */}
          {playersSubTab === 'pseudo' && (
            <Card className="border-purple-300 bg-purple-50/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AtSign className="w-5 h-5 text-purple-500" />
                  Pseudos en attente de validation ({validatedPlayers.filter(p => p.pseudo && !p.pseudo_validated).length})
                </CardTitle>
                <p className="text-sm text-muted-foreground">Ces joueurs ont choisi un pseudo qui doit être validé avant d'apparaître au classement.</p>
              </CardHeader>
              <CardContent>
                {(() => {
                  const playersWithPendingPseudo = validatedPlayers.filter(p => p.pseudo && !p.pseudo_validated);
                  if (playersWithPendingPseudo.length === 0) {
                    return (
                      <div className="text-center py-12">
                        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                        <p className="text-muted-foreground">Aucun pseudo en attente</p>
                        <p className="text-sm text-muted-foreground mt-1">Tous les pseudos ont été validés !</p>
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-2">
                      {playersWithPendingPseudo.map((player) => (
                        <div key={player.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                              {player.first_name?.[0]?.toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium">{player.first_name}</p>
                              <Badge className="bg-purple-100 text-purple-700 border-purple-300">
                                @{player.pseudo}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => handleValidatePseudo(player.id)}>
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Valider
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-500 border-red-200 hover:bg-red-50" onClick={() => handleRejectPseudo(player.id)}>
                              <XCircle className="w-4 h-4 mr-1" />
                              Refuser
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Validated Players Sub-tab */}
          {playersSubTab === 'validated' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-500" />
                  Joueurs validés ({validatedPlayers.length})
                </CardTitle>
                <p className="text-sm text-muted-foreground">Joueurs actifs avec leurs statistiques détaillées et caractères découverts.</p>
              </CardHeader>
              <CardContent>
                {validatedPlayers.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">Aucun joueur validé</p>
                ) : (
                  <div className="space-y-4">
                    {validatedPlayers.map((player) => (
                      <div key={player.id} className="p-4 rounded-xl border bg-gradient-to-r from-slate-50 to-white hover:shadow-md transition-shadow">
                        {/* Header row with player info */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            {player.avatar_url ? (
                              <img src={player.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-cyan-400" />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg">
                                {player.first_name?.[0]?.toUpperCase()}
                              </div>
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-lg">{player.first_name}</p>
                                {player.pseudo && (
                                  <Badge variant="outline" className={player.pseudo_validated ? "text-purple-600 border-purple-300" : "text-gray-500 border-gray-300"}>
                                    @{player.pseudo}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">{player.email}</p>
                              <p className="text-xs text-muted-foreground">{player.company_name} • {player.phone}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={player.is_active ? 'default' : 'secondary'}>
                              {player.is_active ? 'Actif' : 'Inactif'}
                            </Badge>
                            <Button variant="ghost" size="sm" onClick={() => handleTogglePlayerActive(player.id)}>
                              {player.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeletePlayer(player.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Stats row */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
                          <div className="bg-blue-50 rounded-lg p-2 text-center">
                            <p className="text-2xl font-bold text-blue-600">{player.stats?.total_participations || 0}</p>
                            <p className="text-xs text-blue-600/70">Participations</p>
                          </div>
                          <div className="bg-green-50 rounded-lg p-2 text-center">
                            <p className="text-2xl font-bold text-green-600">{player.stats?.total_correct || 0}/{player.stats?.total_questions || 0}</p>
                            <p className="text-xs text-green-600/70">Bonnes réponses</p>
                          </div>
                          <div className="bg-amber-50 rounded-lg p-2 text-center">
                            <p className="text-2xl font-bold text-amber-600">{player.stats?.success_rate || 0}%</p>
                            <p className="text-xs text-amber-600/70">Taux réussite</p>
                          </div>
                          <div className="bg-purple-50 rounded-lg p-2 text-center">
                            <p className="text-2xl font-bold text-purple-600">{player.stats?.total_characters || 0}/24</p>
                            <p className="text-xs text-purple-600/70">Caractères</p>
                          </div>
                          <div className="bg-cyan-50 rounded-lg p-2 text-center">
                            <p className="text-2xl font-bold text-cyan-600">{player.total_score || 0}</p>
                            <p className="text-xs text-cyan-600/70">Score total</p>
                          </div>
                        </div>
                        
                        {/* Characters discovered */}
                        {player.annual_progress?.discovered_chars?.length > 0 && (
                          <div className="bg-slate-100 rounded-lg p-3">
                            <p className="text-xs font-medium text-slate-600 mb-2">
                              📜 Caractères découverts ({player.annual_progress.year}) :
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {player.annual_progress.discovered_chars.map((char, idx) => (
                                <span 
                                  key={idx}
                                  className={`inline-flex items-center px-2 py-1 rounded text-xs font-mono font-bold ${
                                    char.type === 'quiz' 
                                      ? 'bg-red-500 text-white' 
                                      : 'bg-white text-slate-800 border border-slate-300'
                                  }`}
                                  title={`${char.type === 'quiz' ? 'Quiz' : 'Secret'} - Mois ${char.month} - Position ${char.position}`}
                                >
                                  {char.character}
                                  <span className="ml-1 text-[10px] opacity-70">
                                    ({['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Août','Sep','Oct','Nov','Déc'][char.month - 1]})
                                  </span>
                                </span>
                              ))}
                            </div>
                            {/* Show combination progress */}
                            <div className="mt-2 pt-2 border-t border-slate-200">
                              <p className="text-xs text-slate-500 mb-1">Combinaison :</p>
                              <div className="flex flex-wrap gap-0.5">
                                {Array.from({length: 24}).map((_, i) => {
                                  const char = player.annual_progress.combination?.[i];
                                  const isQuiz = i % 2 === 0;
                                  const hasChar = char && char !== '_';
                                  return (
                                    <span 
                                      key={i}
                                      className={`w-5 h-6 flex items-center justify-center text-xs font-mono rounded ${
                                        hasChar 
                                          ? isQuiz 
                                            ? 'bg-red-500 text-white font-bold' 
                                            : 'bg-white text-slate-800 border border-slate-400 font-bold'
                                          : 'bg-slate-200 text-slate-400'
                                      }`}
                                    >
                                      {hasChar ? char : '?'}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* No characters yet */}
                        {(!player.annual_progress?.discovered_chars || player.annual_progress.discovered_chars.length === 0) && (
                          <div className="bg-slate-50 rounded-lg p-3 text-center">
                            <p className="text-sm text-slate-400">Aucun caractère découvert pour {new Date().getFullYear()}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Annual Leaderboard Tab */}
        <TabsContent value="annual" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-500" />
              Jeu Annuel {annualYear}
            </h2>
            <div className="flex items-center gap-2">
              <Select value={String(annualYear)} onValueChange={(v) => setAnnualYear(parseInt(v))}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleEditAnnualGame} className="gap-2">
                {annualGame ? <Edit className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                {annualGame ? 'Modifier' : 'Créer le coffre'}
              </Button>
            </div>
          </div>

          {/* Annual Chest Game Configuration */}
          <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <Lock className="w-5 h-5" />
                {annualGame?.name || 'Le Coffre MSC'} {annualYear}
              </CardTitle>
              <CardDescription>
                Configuration de la combinaison à 24 caractères pour déverrouiller le grand lot
              </CardDescription>
            </CardHeader>
            <CardContent>
              {annualGame ? (
                <div className="space-y-4">
                  {/* Winner display if exists */}
                  {annualGame.winner_id && (
                    <div className="p-4 bg-gradient-to-r from-yellow-100 to-amber-100 rounded-lg border-2 border-amber-400 animate-pulse">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
                          <Trophy className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="text-lg font-bold text-amber-800">🏆 GAGNANT : {annualGame.winner_pseudo}</p>
                          <p className="text-sm text-amber-600">
                            Coffre ouvert le {annualGame.won_at ? new Date(annualGame.won_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '24 décembre'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 p-4 bg-white rounded-lg border border-amber-200 flex-1">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                        <Gift className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-amber-800">{annualGame.prize || 'Lot non défini'}</p>
                        {annualGame.prize_value && (
                          <p className="text-sm font-medium text-amber-700">{annualGame.prize_value} €HT</p>
                        )}
                        <p className="text-sm text-amber-600">{annualGame.prize_description}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button 
                        variant="outline" 
                        onClick={handleGenerateAnnualQuestions}
                        disabled={generatingQuestions}
                        className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                      >
                        {generatingQuestions ? (
                          <><div className="animate-spin rounded-full h-4 w-4 border-2 border-cyan-500 mr-2" /> Génération...</>
                        ) : (
                          <><Sparkles className="w-4 h-4 mr-2" /> Générer les questions IA</>
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-white rounded-lg border border-amber-200">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-amber-800">Combinaison gagnante (24 caractères) :</p>
                      <Badge className="bg-amber-100 text-amber-800 text-xs">Cliquez pour modifier</Badge>
                    </div>
                    <div className="flex flex-wrap gap-1 font-mono text-lg">
                      {annualGame.winning_combination?.split('').map((char, i) => {
                        const monthNum = Math.floor(i/2) + 1;
                        const isQuizChar = i % 2 === 0;
                        const monthNames = ['', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
                        
                        return (
                          <div key={i} className="relative group">
                            <input
                              type="text"
                              maxLength="1"
                              value={char}
                              onChange={async (e) => {
                                const newChar = e.target.value.toUpperCase();
                                if (newChar.length === 1) {
                                  try {
                                    const endpoint = isQuizChar 
                                      ? `/game/cms/annual-games/${annualYear}/quiz-char/${monthNum}`
                                      : `/game/cms/annual-games/${annualYear}/secret-char/${monthNum}`;
                                    await api.put(endpoint, { character: newChar });
                                    toast.success(`Caractère ${monthNames[monthNum]} mis à jour`);
                                    fetchAnnualData(annualYear);
                                  } catch (err) {
                                    toast.error('Erreur lors de la mise à jour');
                                  }
                                }
                              }}
                              className={`w-8 h-10 text-center font-mono text-lg rounded cursor-pointer transition-all hover:scale-110 ${
                                isQuizChar 
                                  ? 'bg-cyan-100 text-cyan-800 border border-cyan-300 hover:bg-cyan-200 focus:ring-2 focus:ring-cyan-500' 
                                  : 'bg-purple-100 text-purple-800 border border-purple-300 hover:bg-purple-200 focus:ring-2 focus:ring-purple-500'
                              }`}
                              title={`${monthNames[monthNum]} - ${isQuizChar ? 'Quiz' : 'Secret'} (cliquez pour modifier)`}
                            />
                            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[9px] text-slate-500 opacity-0 group-hover:opacity-100 whitespace-nowrap">
                              {monthNames[monthNum]}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-amber-600 mt-4">
                      🎮 Cyan = Quiz mensuel | 🔍 Violet = Code mystère | Cliquez sur un caractère pour le modifier
                    </p>
                  </div>
                  
                  <div className="p-4 bg-white rounded-lg border border-amber-200">
                    <p className="text-sm font-medium text-amber-800 mb-2">Indices pour les codes mystères :</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-sm">
                      {annualGame.secret_hints?.map((hint, i) => (
                        <div key={hint.month} className="p-2 bg-purple-50 rounded border border-purple-200">
                          <span className="font-medium text-purple-700">
                            {['', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'][hint.month]}:
                          </span>
                          <span className="text-purple-600 ml-1">{hint.hint || '—'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Lock className="w-12 h-12 mx-auto text-amber-400 mb-4" />
                  <p className="text-amber-600 mb-4">Aucun Jeu Annuel configuré pour {annualYear}</p>
                  <Button onClick={handleEditAnnualGame} className="bg-amber-500 hover:bg-amber-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer le coffre {annualYear}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Annual Game Management Section */}
          {annualGame && (
            <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-800">
                  <Settings className="w-5 h-5" />
                  Gestion du {annualGame?.name || 'Coffre MSC'} {annualYear}
                </CardTitle>
                <CardDescription>
                  Suivi des joueurs, questions et règlement
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Management Tabs */}
                <div className="flex gap-2 mb-4 border-b border-blue-200 pb-2">
                  <Button
                    variant={annualManagementTab === 'players' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setAnnualManagementTab('players')}
                    className={annualManagementTab === 'players' ? 'bg-blue-600' : ''}
                  >
                    <Users className="w-4 h-4 mr-1" />
                    Joueurs ({annualPlayers.length})
                  </Button>
                  <Button
                    variant={annualManagementTab === 'questions' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setAnnualManagementTab('questions')}
                    className={annualManagementTab === 'questions' ? 'bg-blue-600' : ''}
                  >
                    <HelpCircle className="w-4 h-4 mr-1" />
                    Questions ({annualQuestions.reduce((acc, m) => acc + m.questions_count, 0)})
                  </Button>
                  <Button
                    variant={annualManagementTab === 'rules' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setAnnualManagementTab('rules')}
                    className={annualManagementTab === 'rules' ? 'bg-blue-600' : ''}
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Règlement
                  </Button>
                  <Button
                    variant={annualManagementTab === 'stats' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setAnnualManagementTab('stats')}
                    className={annualManagementTab === 'stats' ? 'bg-blue-600' : ''}
                  >
                    <BarChart className="w-4 h-4 mr-1" />
                    Stats
                  </Button>
                </div>

                {loadingAnnualData ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-sm text-muted-foreground mt-2">Chargement...</p>
                  </div>
                ) : (
                  <>
                    {/* Players Tab */}
                    {annualManagementTab === 'players' && (
                      <div className="space-y-3">
                        {annualPlayers.length === 0 ? (
                          <p className="text-center text-muted-foreground py-4">Aucun joueur inscrit pour le moment</p>
                        ) : (
                          <div className="max-h-96 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-blue-100 sticky top-0">
                                <tr>
                                  <th className="text-left p-2">Joueur</th>
                                  <th className="text-center p-2">Progression</th>
                                  <th className="text-center p-2">Quiz</th>
                                  <th className="text-center p-2">Secrets</th>
                                  <th className="text-left p-2">Combinaison</th>
                                </tr>
                              </thead>
                              <tbody>
                                {annualPlayers.map((player, idx) => (
                                  <tr key={player.player_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-blue-50/30'}>
                                    <td className="p-2">
                                      <div className="font-medium">{player.pseudo}</div>
                                      <div className="text-xs text-muted-foreground">{player.company || player.email}</div>
                                    </td>
                                    <td className="text-center p-2">
                                      <div className="flex items-center justify-center gap-2">
                                        <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                                          <div 
                                            className={`h-full ${player.progress_percent === 100 ? 'bg-green-500' : 'bg-amber-500'}`}
                                            style={{ width: `${player.progress_percent}%` }}
                                          />
                                        </div>
                                        <span className="text-xs font-medium">{player.progress_percent}%</span>
                                      </div>
                                    </td>
                                    <td className="text-center p-2">
                                      <Badge variant="outline" className="bg-cyan-50">{player.completed_months}/12</Badge>
                                    </td>
                                    <td className="text-center p-2">
                                      <Badge variant="outline" className="bg-purple-50">{player.secret_months_found}/12</Badge>
                                    </td>
                                    <td className="p-2">
                                      <code className="text-xs font-mono bg-slate-100 px-1 py-0.5 rounded">
                                        {Array.isArray(player.combination) 
                                          ? player.combination.map((c, i) => (
                                              <span key={i} className={c && c !== '' ? 'text-green-600 font-bold' : 'text-slate-400'}>
                                                {c && c !== '' ? c : '_'}
                                              </span>
                                            ))
                                          : player.combination || '________________________'
                                        }
                                      </code>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Questions Tab */}
                    {annualManagementTab === 'questions' && (
                      <div className="space-y-4">
                        {annualQuestions.length === 0 ? (
                          <p className="text-center text-muted-foreground py-4">Aucune question générée</p>
                        ) : (
                          <div className="max-h-96 overflow-y-auto space-y-4">
                            {annualQuestions.map((monthData) => (
                              <div key={monthData.month} className="border rounded-lg p-3 bg-white">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="font-medium flex items-center gap-2">
                                    <Badge className="bg-cyan-500">{monthData.month}</Badge>
                                    {monthData.service_category}
                                  </h4>
                                  <span className="text-xs text-muted-foreground">
                                    {monthData.questions_count} questions
                                  </span>
                                </div>
                                <div className="space-y-2">
                                  {monthData.questions.slice(0, 3).map((q, i) => (
                                    <div key={i} className="text-sm p-2 bg-slate-50 rounded">
                                      <p className="font-medium">Q{i+1}: {q.question}</p>
                                      <div className="grid grid-cols-2 gap-1 mt-1 text-xs">
                                        {q.options?.map((opt, j) => (
                                          <span key={j} className={`px-2 py-0.5 rounded ${j === q.correct_answer ? 'bg-green-100 text-green-700 font-medium' : 'bg-slate-100'}`}>
                                            {opt}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                  {monthData.questions.length > 3 && (
                                    <p className="text-xs text-muted-foreground text-center">
                                      +{monthData.questions.length - 3} autres questions
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Rules Tab */}
                    {annualManagementTab === 'rules' && (
                      <div className="space-y-4">
                        <div className="flex gap-2">
                          <Button 
                            onClick={handleGenerateRules} 
                            disabled={generatingRules}
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            {generatingRules ? (
                              <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white mr-2" /> Génération...</>
                            ) : (
                              <><Sparkles className="w-4 h-4 mr-2" /> Générer le règlement (IA)</>
                            )}
                          </Button>
                          <Button onClick={handleSaveRules} variant="outline">
                            <Save className="w-4 h-4 mr-2" />
                            Sauvegarder
                          </Button>
                        </div>
                        <Textarea
                          value={annualRules}
                          onChange={(e) => setAnnualRules(e.target.value)}
                          placeholder="Le règlement du jeu sera affiché ici..."
                          rows={15}
                          className="font-mono text-sm"
                        />
                      </div>
                    )}

                    {/* Stats Tab */}
                    {annualManagementTab === 'stats' && annualStats && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-4 bg-white rounded-lg border text-center">
                          <Users className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                          <p className="text-2xl font-bold">{annualStats.stats?.total_players || 0}</p>
                          <p className="text-xs text-muted-foreground">Joueurs inscrits</p>
                        </div>
                        <div className="p-4 bg-white rounded-lg border text-center">
                          <Trophy className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                          <p className="text-2xl font-bold">{annualStats.stats?.complete_combinations || 0}</p>
                          <p className="text-xs text-muted-foreground">Combinaisons complètes</p>
                        </div>
                        <div className="p-4 bg-white rounded-lg border text-center">
                          <HelpCircle className="w-8 h-8 mx-auto text-cyan-500 mb-2" />
                          <p className="text-2xl font-bold">{annualQuestions.reduce((a, m) => a + m.questions_count, 0)}</p>
                          <p className="text-xs text-muted-foreground">Questions générées</p>
                        </div>
                        <div className="p-4 bg-white rounded-lg border text-center">
                          <Gift className="w-8 h-8 mx-auto text-green-500 mb-2" />
                          <p className="text-lg font-bold">{annualStats.prize || '-'}</p>
                          <p className="text-xs text-muted-foreground">Lot à gagner</p>
                        </div>
                        
                        {annualStats.winner && (
                          <div className="col-span-full p-4 bg-gradient-to-r from-yellow-100 to-amber-100 rounded-lg border-2 border-amber-400">
                            <div className="flex items-center gap-3">
                              <Trophy className="w-12 h-12 text-amber-500" />
                              <div>
                                <p className="text-lg font-bold text-amber-800">🏆 GAGNANT : {annualStats.winner.pseudo}</p>
                                <p className="text-sm text-amber-600">
                                  Coffre ouvert le {new Date(annualStats.winner.won_at).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Reset Section */}
                        <div className="col-span-full mt-6 pt-6 border-t">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-red-600 flex items-center gap-2">
                                <AlertCircle className="w-5 h-5" />
                                Zone Dangereuse
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                Réinitialiser les données du jeu (progression des joueurs, classements)
                              </p>
                            </div>
                            <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
                              <DialogTrigger asChild>
                                <Button variant="destructive" className="bg-red-600 hover:bg-red-700">
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Réinitialiser
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle className="text-red-600 flex items-center gap-2">
                                    <AlertCircle className="w-5 h-5" />
                                    Réinitialiser les données du Coffre MSC {annualYear}
                                  </DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <p className="text-sm text-red-800 font-medium">⚠️ ATTENTION : Cette action est irréversible !</p>
                                    <p className="text-xs text-red-600 mt-1">
                                      Les données supprimées ne pourront pas être récupérées.
                                    </p>
                                  </div>
                                  
                                  <div className="space-y-3">
                                    <Label className="font-semibold">Données à supprimer :</Label>
                                    <div className="flex items-center gap-2">
                                      <Switch
                                        checked={resetPlayers}
                                        onCheckedChange={setResetPlayers}
                                      />
                                      <span className="text-sm">Progression des joueurs (combinaisons, caractères découverts)</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Switch
                                        checked={resetScores}
                                        onCheckedChange={setResetScores}
                                      />
                                      <span className="text-sm">Scores des quiz mensuels (classements)</span>
                                    </div>
                                  </div>
                                  
                                  <div className="space-y-2">
                                    <Label className="text-sm">Tapez <strong className="text-red-600">CONFIRMER</strong> pour valider :</Label>
                                    <Input
                                      value={resetConfirmText}
                                      onChange={(e) => setResetConfirmText(e.target.value)}
                                      placeholder="CONFIRMER"
                                      className="border-red-200 focus:border-red-500"
                                    />
                                  </div>
                                </div>
                                <DialogFooter>
                                  <Button variant="outline" onClick={() => {
                                    setResetDialogOpen(false);
                                    setResetConfirmText('');
                                  }}>
                                    Annuler
                                  </Button>
                                  <Button 
                                    variant="destructive" 
                                    onClick={handleResetGameData}
                                    disabled={resetting || resetConfirmText !== 'CONFIRMER' || (!resetPlayers && !resetScores)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    {resetting ? (
                                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Suppression...</>
                                    ) : (
                                      <><Trash2 className="w-4 h-4 mr-2" /> Supprimer définitivement</>
                                    )}
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Monthly Games Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quiz mensuels {annualYear}</CardTitle>
              <CardDescription>Aperçu des 12 quiz de l&apos;année</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2">
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(month => {
                  const game = periodicGames.find(g => g.month === month);
                  const monthNames = ['', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];
                  return (
                    <div
                      key={month}
                      className={`p-2 rounded-lg text-center text-xs ${
                        game?.is_finished ? 'bg-slate-200 text-slate-600' :
                        game?.is_active ? 'bg-purple-100 text-purple-700 border-2 border-purple-500' :
                        game ? 'bg-slate-100 text-slate-500' :
                        'bg-slate-50 text-slate-400 border border-dashed'
                      }`}
                    >
                      <div className="font-semibold">{monthNames[month]}</div>
                      {game ? (
                        <div className="mt-1">
                          {game.is_finished ? '✅' : game.is_active ? '▶️' : '⏸️'}
                          <div className="text-[10px]">{game.participants_count || 0} joueurs</div>
                        </div>
                      ) : (
                        <div className="mt-1 text-slate-300">—</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Annual Leaderboard */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                Classement cumulé {annualYear}
              </CardTitle>
              <CardDescription>
                Basé sur la somme des points des quiz mensuels
              </CardDescription>
            </CardHeader>
            <CardContent>
              {annualLeaderboard.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  Aucune participation aux quiz périodiques pour {annualYear}
                </p>
              ) : (
                <div className="space-y-2">
                  {annualLeaderboard.map((entry, idx) => (
                    <div
                      key={entry.player_id}
                      className={`flex items-center justify-between p-4 rounded-lg ${
                        idx === 0 ? 'bg-amber-50 border-2 border-amber-300' :
                        idx === 1 ? 'bg-slate-100 border border-slate-300' :
                        idx === 2 ? 'bg-orange-50 border border-orange-200' :
                        'bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-2xl w-10 text-center">
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${entry.rank}`}
                        </div>
                        <div>
                          <p className="font-semibold">{entry.player_name}</p>
                          <p className="text-sm text-muted-foreground">{entry.company_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-purple-600">{entry.total_score?.toLocaleString()} pts</p>
                        <p className="text-xs text-muted-foreground">
                          {entry.games_played} quiz • {entry.total_correct}/{entry.total_questions} bonnes réponses
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Winners Tab */}
        <TabsContent value="winners">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-500" />
                  Gagnants par jeu
                </div>
                <Select 
                  value={selectedContest?.id || 'all'} 
                  onValueChange={(v) => {
                    if (v === 'all') {
                      setSelectedContest(null);
                    } else {
                      const contest = contests.find(c => c.id === v);
                      if (contest) setSelectedContest(contest);
                    }
                  }}
                >
                  <SelectTrigger className="w-[300px]">
                    <SelectValue placeholder="Tous les jeux">
                      {selectedContest?.title || 'Tous les jeux'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les jeux</SelectItem>
                    {contests.map((contest) => (
                      <SelectItem key={contest.id} value={contest.id}>
                        {contest.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {winners.filter(w => !selectedContest || w.game_id === selectedContest.id).length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  {selectedContest ? `Aucun gagnant pour "${selectedContest.title}"` : 'Aucun gagnant pour le moment'}
                </p>
              ) : (
                <div className="space-y-3">
                  {winners.filter(w => !selectedContest || w.game_id === selectedContest.id).map((winner) => (
                    <div key={winner.id} className="flex items-center justify-between p-4 rounded-lg border bg-gradient-to-r from-amber-50 to-orange-50">
                      <div className="flex items-center gap-3">
                        <div className="text-2xl">
                          {winner.rank === 1 ? '🥇' : winner.rank === 2 ? '🥈' : '🥉'}
                        </div>
                        <div>
                          <p className="font-semibold">{winner.player_name}</p>
                          <p className="text-sm text-muted-foreground">{winner.company_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-amber-600">{winner.game_title}</p>
                        <p className="text-sm">{winner.score} points</p>
                        <p className="text-xs text-muted-foreground">{new Date(winner.created_at).toLocaleDateString('fr-FR')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rules Tab */}
        <TabsContent value="rules">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Règlement du jeu concours
              </CardTitle>
              <CardDescription>
                Ce règlement sera affiché aux joueurs lors de leur inscription
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* AI Generation Section */}
              <div className="p-4 rounded-lg border border-purple-200 bg-purple-50/50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Wand2 className="w-5 h-5 text-purple-500" />
                    <span className="font-medium text-purple-700">Génération par IA</span>
                  </div>
                  {selectedContest && (
                    <Badge variant="outline" className="text-purple-600 border-purple-300">
                      Pour : {selectedContest.title}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Sélectionnez un jeu dans l&apos;onglet &quot;Jeux&quot; puis cliquez pour générer automatiquement un règlement adapté.
                </p>
                <Button 
                  onClick={handleGenerateContestRules}
                  disabled={rulesGenerating || !selectedContest}
                  className="bg-gradient-to-r from-purple-500 to-indigo-600"
                >
                  {rulesGenerating ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white mr-2" /> Génération...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Générer le règlement avec l&apos;IA</>
                  )}
                </Button>
              </div>
              
              <Textarea
                value={rules}
                onChange={(e) => setRules(e.target.value)}
                placeholder="Rédigez le règlement complet du jeu concours..."
                rows={20}
                className="font-mono text-sm"
              />
              <Button onClick={handleSaveRules}>
                Enregistrer le règlement
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
        </div>
      )}

      {/* Annual Game Dialog */}
      <Dialog open={annualGameDialogOpen} onOpenChange={setAnnualGameDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-500" />
              {annualGame ? 'Modifier' : 'Créer'} le Jeu Annuel {annualGameForm.year}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Game Name */}
            <div className="space-y-2">
              <Label htmlFor="annual-name">Nom du jeu</Label>
              <Input
                id="annual-name"
                value={annualGameForm.name}
                onChange={(e) => setAnnualGameForm({...annualGameForm, name: e.target.value})}
                placeholder="Ex: Le Coffre MSC"
              />
            </div>
            
            {/* Prize */}
            <div className="space-y-2">
              <Label htmlFor="annual-prize">Gros lot à gagner</Label>
              <Input
                id="annual-prize"
                value={annualGameForm.prize}
                onChange={(e) => setAnnualGameForm({...annualGameForm, prize: e.target.value})}
                placeholder="Ex: iPhone 15 Pro + 1000€"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="annual-prize-value">Valeur du lot (€HT)</Label>
              <Input
                id="annual-prize-value"
                type="number"
                value={annualGameForm.prize_value || ''}
                onChange={(e) => setAnnualGameForm({...annualGameForm, prize_value: e.target.value})}
                placeholder="Ex: 1500"
                min={0}
                step={0.01}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="annual-prize-desc">Description du lot</Label>
              <Textarea
                id="annual-prize-desc"
                value={annualGameForm.prize_description}
                onChange={(e) => setAnnualGameForm({...annualGameForm, prize_description: e.target.value})}
                placeholder="Détails sur le lot..."
                rows={2}
              />
            </div>
            
            {/* Winning Combination */}
            <div className="space-y-2">
              <Label htmlFor="annual-combo" className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                Combinaison gagnante (24 caractères)
              </Label>
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 mb-2">
                <p className="text-xs text-amber-700">
                  <strong>Format :</strong> Alternez les caractères : Quiz Janvier, Secret Janvier, Quiz Février, Secret Février, etc.
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  Exemple : A1B2C3D4E5F6G7H8I9J0K1L2 (positions impaires = quiz, positions paires = secrets)
                </p>
              </div>
              <div className="flex gap-2">
                <Input
                  id="annual-combo"
                  value={annualGameForm.winning_combination}
                  onChange={(e) => setAnnualGameForm({...annualGameForm, winning_combination: e.target.value.toUpperCase().slice(0, 24)})}
                  placeholder="24 caractères..."
                  maxLength={24}
                  className="font-mono text-lg tracking-widest flex-1"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={generateRandomCombination}
                  className="whitespace-nowrap"
                >
                  🎲 Générer
                </Button>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className={annualGameForm.winning_combination.length === 24 ? 'text-green-600' : 'text-amber-600'}>
                  {annualGameForm.winning_combination.length}/24 caractères
                </span>
                {annualGameForm.winning_combination.length === 24 && (
                  <span className="text-green-600">✓ Combinaison complète</span>
                )}
              </div>
              
              {/* Visual preview of combination */}
              {annualGameForm.winning_combination && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {annualGameForm.winning_combination.split('').map((char, i) => (
                    <span 
                      key={i} 
                      className={`w-7 h-8 flex items-center justify-center rounded text-sm font-mono ${
                        i % 2 === 0 ? 'bg-cyan-100 text-cyan-800' : 'bg-purple-100 text-purple-800'
                      }`}
                    >
                      {char}
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            {/* Auto-create quizzes option (only for new games) */}
            {!annualGame && (
              <div className="space-y-3 p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="auto-quizzes"
                    checked={annualGameForm.auto_create_quizzes}
                    onChange={(e) => setAnnualGameForm({...annualGameForm, auto_create_quizzes: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="auto-quizzes" className="text-cyan-800 font-medium">
                    Créer automatiquement les 12 quiz mensuels
                  </Label>
                </div>
                {annualGameForm.auto_create_quizzes && (
                  <div className="space-y-2 pl-6">
                    <Label className="text-sm text-cyan-700">Thème des questions (5 questions/mois)</Label>
                    <Input
                      value={annualGameForm.quiz_topic}
                      onChange={(e) => setAnnualGameForm({...annualGameForm, quiz_topic: e.target.value})}
                      placeholder="Ex: services de l'agence MSC"
                      className="bg-white"
                    />
                    <p className="text-xs text-cyan-600">
                      Les questions seront générées automatiquement par IA après la création.
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {/* Secret Hints */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Indices pour les codes mystères
              </Label>
              <p className="text-xs text-muted-foreground mb-2">
                Donnez des indications aux joueurs sur où trouver les codes secrets (réseaux sociaux, bureaux, téléphone...)
              </p>
              <div className="grid grid-cols-2 gap-2">
                {annualGameForm.secret_hints.map((hint, idx) => {
                  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
                  return (
                    <div key={hint.month} className="flex items-center gap-2">
                      <span className="text-xs font-medium w-16 text-purple-600">{monthNames[idx]}:</span>
                      <Input
                        value={hint.hint}
                        onChange={(e) => {
                          const newHints = [...annualGameForm.secret_hints];
                          newHints[idx] = { ...newHints[idx], hint: e.target.value };
                          setAnnualGameForm({...annualGameForm, secret_hints: newHints});
                        }}
                        placeholder="Ex: Sur Instagram"
                        className="text-sm h-8"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setAnnualGameDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSaveAnnualGame}
              disabled={annualGameForm.winning_combination.length !== 24}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {annualGame ? 'Mettre à jour' : 'Créer le coffre'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== MODAL TIRAGE AU SORT ==================== */}
      <Dialog open={drawModalOpen} onOpenChange={setDrawModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Shuffle className="w-6 h-6 text-purple-500" />
              🎲 Tirage au sort
            </DialogTitle>
            {drawContest && (
              <p className="text-muted-foreground">{drawContest.title}</p>
            )}
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Info du lot */}
            {drawContest && (
              <div className="text-center p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 rounded-xl">
                <Gift className="w-12 h-12 mx-auto text-purple-500 mb-2" />
                <h3 className="text-xl font-bold">{drawContest.prize}</h3>
                {drawContest.prize_value && (
                  <p className="text-muted-foreground">Valeur : {drawContest.prize_value}€</p>
                )}
              </div>
            )}
            
            {/* Animation de tirage */}
            {drawAnimation && !drawResult && (
              <div className="text-center py-8">
                <div className="relative w-32 h-32 mx-auto">
                  <div className="absolute inset-0 rounded-full border-4 border-purple-500 border-t-transparent animate-spin"></div>
                  <div className="absolute inset-2 rounded-full border-4 border-pink-500 border-b-transparent animate-spin" style={{ animationDirection: 'reverse', animationDuration: '0.8s' }}></div>
                  <div className="absolute inset-4 rounded-full border-4 border-amber-500 border-t-transparent animate-spin" style={{ animationDuration: '0.5s' }}></div>
                  <Shuffle className="absolute inset-0 m-auto w-10 h-10 text-purple-600 animate-pulse" />
                </div>
                <p className="mt-4 text-lg font-semibold text-purple-600 animate-pulse">
                  Tirage en cours...
                </p>
                <p className="text-sm text-muted-foreground">
                  Parmi {drawParticipants.length} participant{drawParticipants.length > 1 ? 's' : ''}
                </p>
              </div>
            )}
            
            {/* Résultat du tirage */}
            {drawResult && (
              <div className="text-center py-6">
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-purple-500 via-pink-500 to-amber-500 rounded-2xl blur-xl opacity-30 animate-pulse"></div>
                  <div className="relative bg-white dark:bg-slate-900 rounded-xl p-8 border-2 border-purple-500">
                    <Trophy className="w-16 h-16 mx-auto text-amber-500 mb-4" />
                    <h3 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                      🎉 FÉLICITATIONS !
                    </h3>
                    <div className="mt-4 space-y-2">
                      <p className="text-2xl font-bold">{drawResult.winner?.pseudo || drawResult.winner?.name}</p>
                      {drawResult.winner?.company && (
                        <p className="text-muted-foreground">{drawResult.winner.company}</p>
                      )}
                      <p className="text-sm text-muted-foreground">{drawResult.winner?.email}</p>
                    </div>
                    <div className="mt-6 flex gap-2 justify-center">
                      <Button
                        size="sm"
                        onClick={() => handleNotifyGameResults(drawContest.id)}
                        disabled={sendingNotification}
                        className="bg-blue-500 hover:bg-blue-600"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Envoyer résultats aux participants
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Liste des participants (avant tirage) */}
            {!drawAnimation && !drawResult && (
              <>
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {drawParticipants.length} Participant{drawParticipants.length > 1 ? 's' : ''}
                  </h4>
                  <div className="max-h-48 overflow-y-auto bg-slate-50 dark:bg-slate-900 rounded-lg p-3">
                    {drawParticipants.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">Aucun participant</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {drawParticipants.map((p, i) => (
                          <div key={i} className="text-sm p-2 bg-white dark:bg-slate-800 rounded flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center text-xs font-bold text-purple-600">
                              {i + 1}
                            </span>
                            <span className="truncate">{p.pseudo || p.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Rappels manuels */}
                {drawContest?.draw_datetime && (
                  <div className="space-y-2 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                    <h4 className="font-semibold text-amber-700 dark:text-amber-400">📧 Envoyer des rappels</h4>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => sendDrawReminder(drawContest.id, '7days')}
                        disabled={sendingNotification}
                      >
                        Rappel J-7
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => sendDrawReminder(drawContest.id, '1day')}
                        disabled={sendingNotification}
                      >
                        Rappel J-1
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => sendDrawReminder(drawContest.id, 'today')}
                        disabled={sendingNotification}
                      >
                        Rappel Jour J
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDrawModalOpen(false)}>
              Fermer
            </Button>
            {!drawResult && (
              <Button
                onClick={performDraw}
                disabled={isDrawing || drawParticipants.length === 0}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              >
                {isDrawing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Tirage en cours...</>
                ) : (
                  <><Shuffle className="w-4 h-4 mr-2" /> Lancer le tirage !</>
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSGameContestsPage;
